/* eslint-disable max-lines */
/**
 * Transfer service
 */
import { Injectable, Injector } from '@angular/core';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import {
  ceil,
  chain,
  compact,
  concat,
  countBy,
  every,
  filter,
  find,
  first,
  flow,
  forEach,
  get,
  has,
  head,
  includes,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  map,
  merge,
  omit,
  pick,
  range,
  reduce,
  set,
  size,
  sortBy,
  startCase,
  toLower,
  some,
  partition,
  unset,
  findIndex,
  lte,
  uniq,
  keyBy,
  intersectionBy,
  lowerFirst,
  cloneDeep,
} from 'lodash';
import { HttpParams } from '@angular/common/http';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { fromPromise } from 'rxjs/internal-compatibility';
import { iif, of } from 'rxjs';

import {
  InventoryItemVenueMeasure,
  Threshold,
} from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue';
import { InventoryItemVenue } from '@geniuspos/xenial-data-contracts/types/dm-entities';
import { FilterOptionType } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/filter.type';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import {
  BehaviorSubject,
  Observable,
  observableCombineLatest,
  observableOf,
  rx,
} from '@app/shared/rx';
import { DecimalNumber, decimalNumber } from '@app/shared/common';
import {
  TRANSFER_NOT_FINAL_STATUS,
  TRANSFER_UNSAVED_STATUS,
} from '@shared/services/transfer/transfer.definition';
import {
  CELL_FILTER,
  CELL_RENDERER,
  COLUMN_SELECTOR_ID,
  COMMON_ACTION_TYPES,
  EHeaderName,
  ENTITY_TYPES,
  EntityValidateResponse,
  FilterItemsSourceEnum,
  IApiListResponse,
  IBaseEntityResponse,
  IBaseEntityType,
  IBulkUpdateDocumentsResult,
  IDocumentOperationOptions,
  IEntityConfig,
  IEvent,
  IFilterQuery,
  IGridFlatInventoryItemVenue,
  IInventoryItem,
  IModalDialogOptions,
  IModalDialogResult,
  IQohInventoryItemVenue,
  IQuantityOnHandItem,
  IQuantityOnHandResponse,
  ISite,
  ITransfer,
  ITransferData,
  ITransferErrorMapper,
  NotificationType,
  ObjectId,
  TRANSFER_PDF_HEADERS,
  TRANSFER_PDF_HEADERS_FINAL,
  TRANSFER_TYPE,
  XUX_GRID_ACTIONS,
  XuxGridAction,
  IMergeBulkUpdateDocumentsResult,
  TRANSFER_PAR_QTY_COLUMN_HEADER,
  ITask,
  TASK_TYPE,
  ITaskOptions,
  IXVIListColumn,
  IInventoryItemVenue,
  MEASURE_TRANSACTION,
  DUPLICATE_TRANSFER_ERROR_KEY,
  Primitive,
  DuplicateError,
  NotAssignedSites,
  COMMON_ACTION_NAMES,
  TRANSFER_FILTER_STATUS_OPTIONS,
  TRANSFER_STATUS_ENUM_OPTIONS,
  STATUS_ACTION_MAPPING,
  TRANSFER_PDF_HEADER_VALUES,
} from '@shared/types';
import {
  TRANSFER_STATUS_OPTIONS,
  TRANSFER_STATUS_VALUE,
} from '@shared/types/enums/transfer-status.enum';
import {
  flatTreeDataByHasChildren,
  generateObjectId,
  generateSpacesText,
  generateUnitTextV2,
  getSingleExportFileName,
  isNeedDMSync,
  toDate,
  truncationText,
} from '@shared/utils';
import {
  BTN_PRIMARY_CLASS,
  BULK_REQUEST_COUNT,
  ENTITY_ID_PATH,
  MAX_PAGE_SIZE,
  TIME_FORMATS,
} from '@config/global-constants';
import { BTN_CANCEL, BTN_YES } from '@text-content/button-templates';
import { XprtDataService } from '@shared/services/xprt-data/xprt-data.service';
import { lazyInject } from '@app/containers/base/decorators';
import { dataSourceValueFormatter } from '@app/shared/formatters';
import {
  COMMON_FORM_PATH,
  TOASTER_ERROR_MESSAGE,
  TOASTER_SUCCESS_MESSAGE,
  TRANSFER_OPERATION_LOWER_STATUS_MAPPING,
  TRANSFER_OPERATION_STATUS_MAPPING,
} from '@app/shared/constants';
import { PdfBaseService } from '@shared/common/make-pdf';
import { TransferPdfService } from '@shared/services/transfer/transfer.pdf.service';
import { ToolsActions, UiActions } from '@store/actions';
import { XUXListContainerComponent } from '@app/containers/base';
import { markInventoryItemIsUpdated } from '@shared/utils/inventory-item.utils';
import {
  IPdfBulkOptions,
  IPdfDefinitions,
  IPdfOptions,
  PdfSelectColumn,
} from '@app/shared/common/make-pdf/v2/pdf.type';
import { PdfGenerateServiceV2 } from '@app/shared/common/make-pdf/v2/pdf.generate.service';
import { TRANSFER_MODULE } from '@app/routes';
import {
  defaultWidgetData,
  gridSelectModalOptions,
} from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.settings';
import { setFormFieldValue } from '@app/components/form/services/schema-form/schema-form-functions';
import { XuxGridListComponent } from '@app/components/xux/xux-grid-list/xux-grid-list-widget.component';
import { wrapWithLoader } from '@app/shared/common/decorators/wrap-loader.decorator';
import { IPdfTableHeader } from '@app/shared/common/make-pdf/pdf-table-header.type';
import { IPdfData } from '@app/shared/common/make-pdf/v2/pdf-data.type';

import { BaseService } from '../base';
import { NumberFormatService } from '../number-format';
import { SitesService } from '../sites';
import { QohService } from '../qoh/qoh.service';
import {
  appendInitialFactorAndCountValue,
  calculateUnitCost,
  filterQohFromColumn,
  getIsShowReceivedColStatus,
  getQohSiteIdFieldByOverride,
  isSiteTypePrimaryWarehouse,
  isSiteTypeWarehouse,
  isXVIStructureDoc,
  setPrimaryAndWarehouseTop,
  setPrimaryAndWarehouseTopAndAddStoreNumber,
  isOpenOrNewDuringAutoCompleteTransfer,
  transferTypeFormatter,
  getParQty,
  addParQtyColumn,
  getDefaultOrSelectedUnitEntityId,
  addQtyPulledColumn,
} from './transfer.util';
import {
  transferEntityType,
  transferServiceConfig,
} from './transfer.service.config';
import {
  apiItemFields,
  DEPARTMENT_CODE_NOT_CONFIGURED,
  OPEN_AND_PENDING_STATUS,
} from './transfer-constants';
import { InventoryItemVenueService } from '../inventory-item-venue/inventory-item-venue.service';
import { TRANSFER_FORM_FIELD_PATHS } from './transfer-form-field-paths';
import { TransferTotalFormService } from './transfer-totals-form.service';
import {
  getTransferWrittenSheetConfigBuilder,
  TransferWrittenSheetPdfConfigBuilder,
} from './transfer.written-sheet.pdf.config';
import {
  getTransferPdfConfigBuilder,
  getTotalsDefaultValue,
  TransferPdfConfigBuilder,
} from './transfer-pdf-config';
import { DataSource } from '../datasource/entity-data-source.service';
import { EntityServiceProvider } from '../datasource/entity-service-provider.service';
import { buildPrintSheetPdfConfig } from './print-sheet-pdf-config';
import { CompanyUtilService } from '../company/company-util.service';
import { printSheetModalDefinition } from './print-sheet-modal.definition';
import { DmDataService } from '../dm-data/dm-data.service';
import { TaskService } from '../task/task.service';
import { TransferBulkStatusService } from '../transfer-bulk/transfer-bulk-status.service';
import { Router } from '@angular/router';
import { TotalsConfiguration } from '@app/shared/common/make-pdf/v2';

import { IPdfSpecialOptions } from './transfer-export.type';
import { DATA_SOURCE_COL, getTimeAtColDef } from './transfer-column.definition';
import { transferModalDefinition } from './modal.definition';
import { EVENT_STATUS_VALUE } from '@app/shared/types/enums/event-status.enum';
import { getSingularEntityName } from '@app/shared/configs';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

export const statusFormatter = (params: ValueFormatterParams): string =>
  startCase(params.value);

/**
 * Transfer service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class TransferService extends BaseService {
  public static config: IEntityConfig = transferServiceConfig;
  public static ENTITY_TYPE: string = transferServiceConfig.ENTITY_TYPE;

  @lazyInject(ToolsActions) public toolsActions: ToolsActions;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(PdfBaseService) public pdfBaseService: PdfBaseService;
  @lazyInject(TransferPdfService) public transferPdfService: TransferPdfService;
  @lazyInject(QohService) public qohService: QohService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(PdfGenerateServiceV2)
  public pdfGenerateServiceV2: PdfGenerateServiceV2;
  @lazyInject(TransferTotalFormService)
  public transferTotalFormService: TransferTotalFormService;
  @lazyInject(EntityServiceProvider)
  public entityServiceProvider: EntityServiceProvider;
  @lazyInject(CompanyUtilService) public companyUtilService: CompanyUtilService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(TaskService) public taskService: TaskService;
  @lazyInject(TransferBulkStatusService)
  public transferBulkStatusService: TransferBulkStatusService;
  @lazyInject(Router) public router: Router;
  @lazyInject(XVII18nService) public i18nService: XVII18nService;

  public quickFilterFields: string[] = ['name'];
  public listProjection: string[] = ['name'];
  public headerNames: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      filter: false,
    },
  ];
  public events: IEvent[];
  public printAndExportMaxLength: number = 50;
  public needGetUpdateItemsStatus: TRANSFER_STATUS_VALUE[] =
    OPEN_AND_PENDING_STATUS;
  public needSelectedPdfColumns: boolean = true;
  public pdfConfigBuilder: TransferPdfConfigBuilder =
    getTransferPdfConfigBuilder(TRANSFER_FORM_FIELD_PATHS, {
      isFromDetail: false,
      signatureConfiguration: {
        title: {
          text: I18n.Common.Notes,
        },
      },
      totalsConfiguration: {
        enabled: true,
        getDefaultValue: getTotalsDefaultValue,
      },
    });
  public companySitesList$: BehaviorSubject<ISite[]> = new BehaviorSubject<
    ISite[]
  >([]);
  public companySitesList: ISite[] = [];
  public siteModalDescription$: BehaviorSubject<string> =
    new BehaviorSubject<string>(
      this.i18nService.translate(I18n.Forms.GridMessages.ShowingEntities, {
        count: 0,
        entities: this.i18nService.translate(
          I18n.EntityNames.SiteSingularAndPlural,
        ),
      }),
    );

  public transferFromDataSource: DataSource;
  public siteListSelected: number = 0;
  public siteModalTotal: number = 0;
  public siteModalSelected: number = 0;
  public summaryTitleFontSize: number = 8;
  public inventoryItemGroupResponse: IBaseEntityResponse<IInventoryItem>;
  public pdfWrittenSheetConfigBuilder: TransferWrittenSheetPdfConfigBuilder =
    getTransferWrittenSheetConfigBuilder(TRANSFER_FORM_FIELD_PATHS);
  public transferAutoRestockUri: string = `/${TRANSFER_MODULE}/auto-restock`;
  public printSheetsModalForm: FormGroup;
  public siteFromDataSource: IApiListResponse;

  public constructor(public injector: Injector) {
    super(injector);

    this.config.buttons = {
      edit: {
        hidden: true,
      },
      bulk_submit: {
        caption: I18n.Common.Submit,
        hidden: this.isHiddenBulkSubmit.bind(this),
        click: this.getBulkUpdateStatusFn('submit', 'submitted', true).bind(
          this,
        ),
      },
      bulk_cancel: {
        value: COMMON_ACTION_NAMES.Cancel,
        caption: I18n.Forms.TransferForm.CancelTransfer,
        hidden: true,
        click: this.getBulkUpdateStatusFn('cancel', 'canceled').bind(this),
      },
      bulk_print: {
        caption: I18n.Common.Print,
        hidden(selectedItems: IBaseEntityType[]): boolean {
          return selectedItems.length < 1;
        },
        click: this.bulkGenerateTransferSummary(false),
      },
      bulk_export: {
        caption: I18n.Common.ExportPDF,
        hidden(selectedItems: IBaseEntityType[]): boolean {
          return selectedItems.length < 1;
        },
        click: this.bulkGenerateTransferSummary(false, true),
      },
      bulk_print_written_sheet: {
        caption: I18n.Common.PrintWrittenSheet,
        hidden(selectedItems: IBaseEntityType[]): boolean {
          return !(
            selectedItems.length &&
            every(selectedItems, (item) =>
              includes(OPEN_AND_PENDING_STATUS, item.status),
            )
          );
        },
        click: this.bulkGenerateTransferSummary(true),
      },
      bulk_void: {
        caption: I18n.Status.Void,
        hidden: this.bulkVoidHidden.bind(this),
        click: this.bulkVoidHandler.bind(this),
      },
      auto_restock: {
        caption: I18n.Common.AutoRestock,
        hidden: this.autoRestockHidden.bind(this),
        click: this.openAutoRestock.bind(this),
      },
      print_sheets: { click: this.printSheetsBtnClickHandler },
      duplicate: {
        caption: I18n.Common.Duplicate,
        click: this.duplicateButtonClick.bind(this),
        hidden(selectedItems: IBaseEntityType[]): boolean {
          return !(
            this.permissionService.canAdd(TransferService.ENTITY_TYPE) &&
            this.entityType === ENTITY_TYPES.TRANSFER &&
            selectedItems.length === 1 &&
            every(selectedItems, (item) =>
              includes(
                [...OPEN_AND_PENDING_STATUS, TRANSFER_STATUS_VALUE.Completed],
                item.status,
              ),
            )
          );
        },
      },
    };
    this.config.list.columns.push(
      ...(<IXVIListColumn[]>[
        {
          path: 'transaction_date',
          title: I18n.Common.Date,
          filterType: CELL_FILTER.dateLocale,
          sortable: true,
          defaultView: true,
        },
        {
          path: 'internal_number',
          title: I18n.Forms.GridForm.ID,
          filterType: 'stringFilter',
          filterParams: {
            quickFilter: true,
          },
          sortable: true,
          defaultView: true,
        },
        DATA_SOURCE_COL({
          i18nService: this.i18nService,
        }),
        {
          path: 'site_id',
          labelPath: 'site_name',
          sortUseLabelPath: true,
          title: I18n.Forms.TransferForm.FromNameBrackets,
          filterType: CELL_FILTER.entity,
          filterParams: {
            quickFilter: true,
            default: {
              type: 'containsAny' as FilterOptionType,
            },
            suppressSelectAllControl: true,
            filterItemsSource: FilterItemsSourceEnum.XPRT_SITES,
            filterOptions: ['containsAny'] as FilterOptionType[],
            filterItemSourceQuery: {
              select: ['name', 'id'],
            },
            suppressUIFiltering: true,
          },
          cellRenderer: CELL_RENDERER.link_renderer,
          cellRendererParams: {
            entityType: transferEntityType,
            needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
          },
          sortable: true,
          defaultView: true,
        },
        {
          path: 'store_number',
          title: I18n.Forms.TransferForm.FromIDBrackets,
          filterType: 'stringFilter',
          filterParams: {
            quickFilter: true,
          },
          cellRenderer: CELL_RENDERER.link_renderer,
          cellRendererParams: {
            entityType: transferEntityType,
            needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
          },
          sortable: true,
        },
        {
          path: 'site_id_to',
          labelPath: 'site_name_to',
          sortUseLabelPath: true,
          title: I18n.Forms.TransferForm.ToNameBrackets,
          filterType: CELL_FILTER.entity,
          filterParams: {
            quickFilter: true,
            default: {
              type: 'containsAny' as FilterOptionType,
            },
            suppressSelectAllControl: true,
            filterItemsSource: FilterItemsSourceEnum.XPRT_SITES,
            filterOptions: ['containsAny'] as FilterOptionType[],
            filterItemSourceQuery: {
              select: ['name', 'id'],
            },
            suppressUIFiltering: true,
          },
          cellRenderer: CELL_RENDERER.link_renderer,
          cellRendererParams: {
            entityType: transferEntityType,
            needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
          },
          sortable: true,
          defaultView: true,
        },
        {
          path: 'store_number_to',
          title: I18n.Forms.TransferForm.ToIDBrackets,
          filterType: 'stringFilter',
          filterParams: {
            quickFilter: true,
          },
          cellRenderer: CELL_RENDERER.link_renderer,
          cellRendererParams: {
            entityType: transferEntityType,
            needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
          },
          sortable: true,
        },
        {
          path: 'transfer_type',
          title: I18n.Common.Type,
          filterType: CELL_FILTER.enum,
          filterParams: {
            values: TRANSFER_FILTER_STATUS_OPTIONS,
          },
          sortable: true,
          valueFormatter: (params) =>
            transferTypeFormatter(params, { i18nService: this.i18nService }),
          defaultView: true,
        },
        {
          path: 'event_id',
          title: I18n.EntityNames.Event,
          defaultView: true,
          filterType: CELL_FILTER.entity,
          filterParams: {
            default: {
              type: 'containsAny' as FilterOptionType,
            },
            suppressSelectAllControl: true,
            filterItemsSource: FilterItemsSourceEnum.XPRT_EVENTS,
            filterOptions: ['containsAny'] as FilterOptionType[],
            filterItemSourceQuery: {
              select: ['name', 'id'],
            },
            suppressUIFiltering: true,
          },
        },
        {
          path: 'status',
          title: I18n.Common.Status,
          sortable: true,
          defaultView: true,
          filterType: CELL_FILTER.enum,
          filterParams: {
            values: filter(
              TRANSFER_STATUS_OPTIONS,
              (item) => !includes(TRANSFER_UNSAVED_STATUS, item.value),
            ),
          },
          cellRenderer: CELL_RENDERER.prepared_string_renderer,
          cellRendererParams: {
            valueToEnumMap: TRANSFER_STATUS_ENUM_OPTIONS,
          },
        },
        getTimeAtColDef({
          companyService: this.companyService,
          i18nService: this.i18nService,
        }),
        {
          path: COLUMN_SELECTOR_ID,
          width: 50,
          title: '',
          defaultView: true,
          cellRenderer: CELL_RENDERER.transfer_actions_renderer,
          pinned: detectMobile() ? false : 'right',
          lockPinned: true,
        },
      ]),
    );
  }

  public isHiddenBulkSubmit(selectedItems: IBaseEntityType[]): boolean {
    return (
      !this.canManage ||
      !(
        selectedItems.length &&
        every(selectedItems, (item) =>
          isEqual(TRANSFER_STATUS_VALUE.Open, item.status),
        )
      )
    );
  }

  public initTransferFromSource(): void {
    this.transferFromDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.SITE,
    );
    this.transferFromDataSource.fetchList = (): Promise<IApiListResponse> =>
      Promise.resolve(this.siteFromDataSource);
  }

  public siteFromDataSourceFetchList(): Promise<IApiListResponse> {
    const queryWithDefaults: IFilterQuery = {
      filter: `is_active eq true`,
      select: [
        'name',
        'id',
        'type',
        'business_type',
        'store_number',
        'metadata',
      ],
      filter_by_assigned_sites: true,
      include_inactive: false,
      top: MAX_PAGE_SIZE,
    };

    return this.sitesService
      .fetchList(queryWithDefaults)
      .pipe(
        rx.first(),
        rx.map((response) => {
          this.companySitesList = sortBy(response.items, (site) =>
            toLower(site.name),
          ) as ISite[];
          response.items = setPrimaryAndWarehouseTopAndAddStoreNumber(
            this.companySitesList,
          );
          response.total = size(response.items);

          return response;
        }),
      )
      .toPromise()
      .then(
        (result) => result,
        (error) => {
          this.transferFromDataSource.error.next(error);

          return Promise.reject(error);
        },
      );
  }

  public async openPrintSheetModal(): Promise<IModalDialogResult> {
    const siteSelectorGridSettings: IModalDialogOptions =
      printSheetModalDefinition.selectSiteAndToSitesModal(this);
    const modalOptions: IModalDialogOptions = merge(
      {
        schema: {
          properties: {
            site_id: {},
            items: {},
          },
        },
      },
      defaultWidgetData,
      gridSelectModalOptions(this.companySitesList$, this.i18nService),
      siteSelectorGridSettings,
      {
        onFormReady: this.popWindowFormReady(),
      },
    );
    const dialogResult = await this.uiActions.promptFormModal(modalOptions);

    return dialogResult;
  }

  public listModalGridAction(
    event: XuxGridAction,
    grid: XuxGridListComponent,
  ): void {
    switch (event.type) {
      case XUX_GRID_ACTIONS.GRID_READY:
        this.siteModalSelected = 0;
        this.siteModalTotal = event.payload.gridApi.getDisplayedRowCount();
        this.siteModalDescription$.next(
          this.getSitesListText(
            this.siteModalSelected,
            this.siteModalTotal,
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        );
        break;
      case XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED:
        this.siteModalSelected = 0;
        this.siteModalTotal = event.payload?.rowData?.length || 0;
        this.siteModalDescription$.next(
          this.getSitesListText(
            this.siteModalSelected,
            this.siteModalTotal,
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        );
        break;
      case XUX_GRID_ACTIONS.ROW_SELECTION_CHANGED:
        this.siteModalSelected = event.payload?.rowData?.length || 0;
        this.siteModalDescription$.next(
          this.getSitesListText(
            this.siteModalSelected,
            this.siteModalTotal,
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        );
        break;
      case XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED:
        this.siteModalSelected = event.payload?.rowData?.length || 0;
        this.setSubtitleForModel(grid);
        break;
      case XUX_GRID_ACTIONS.FILTER_MODEL_CHANGED:
        this.setSubtitleForModel(grid);
        break;
      default:
        return;
    }
  }

  public getToSiteListRequireValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlRoot = control.root;
      const controlParent = control.parent;

      if (isNil(controlRoot.value) || isNil(controlParent)) return null;

      return !isEmpty(control.value)
        ? null
        : {
            error: this.i18nService.translate(
              I18n.ErrorMessages.TransferNoSelectedToSitesError,
            ),
          };
    };
  }

  public setSubtitleForModel(grid: XuxGridListComponent): void {
    this.subscriptions.push(
      grid.visibleItemCount$.subscribe((totalCountVisible: number) => {
        this.siteModalTotal = totalCountVisible;
        this.siteModalDescription$.next(
          this.getSitesListText(
            this.siteModalSelected,
            this.siteModalTotal,
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        );
      }),
    );
  }

  public getSitesListText(
    siteListSelected: number,
    siteListTotal: number,
    entity: string,
  ): string {
    const params = {
      count: siteListTotal,
      entities: this.i18nService.translate(entity),
    };

    const isZeroSelected = isEqual(siteListSelected, 0);

    return this.i18nService.translate(
      isZeroSelected
        ? I18n.Forms.GridMessages.ShowingEntities
        : I18n.Forms.GridMessages.ShowingEntitiesWithSelected,
      isZeroSelected ? params : { ...params, selectedCount: siteListSelected },
    );
  }

  public popWindowFormReady(): Function {
    return (form: FormGroup): void => {
      this.printSheetsModalForm = form;
      setFormFieldValue(form, 'site_id', '', false);
      setFormFieldValue(form, 'items', [], false);
      form.controls.site_id.valueChanges
        .pipe(
          rx.filter(Boolean),
          rx.tap(() => {
            this.companySitesList$.next([]);
          }),
          rx.map((value) => {
            return {
              currentItem: find(this.companySitesList, ['id', value]),
              filteredByFromSite: filter(
                this.companySitesList,
                (item) => !isEqual(item.id, value),
              ),
            };
          }),
          rx.mergeMap(({ currentItem, filteredByFromSite }) => {
            return isSiteTypePrimaryWarehouse(currentItem)
              ? this.getPrimaryWarehouseList(filteredByFromSite, currentItem)
              : Observable.from([filteredByFromSite]);
          }),
        )
        .subscribe((data: ISite[]) => {
          this.companySitesList$.next(data);
        });
      const headElementOfSites = head(
        setPrimaryAndWarehouseTopAndAddStoreNumber(this.companySitesList),
      );
      const hasPrimaryWarehouse =
        isSiteTypePrimaryWarehouse(headElementOfSites);
      const id = hasPrimaryWarehouse ? get(headElementOfSites, 'id') : null;
      setFormFieldValue(this.printSheetsModalForm, 'site_id', id || '', true);
    };
  }

  public getPrimaryWarehouseList(
    filteredByFromSite: ISite[],
    currentItem: ISite,
  ): Observable<ISite[]> {
    const siteIds = map(filteredByFromSite, 'id');
    this.uiActions.enableModalLoader();

    return this.dmDataService.getSiteGroupId(siteIds).pipe(
      rx.switchMap((siteGroupId) =>
        this.dmDataService.getSitesPreferencesBySiteGroupId(siteGroupId),
      ),
      rx.mergeMap((sitePreferences) => {
        const siteFromId = currentItem.id;
        const sitePrimaryWarehouseMap = reduce(
          sitePreferences,
          (result, sitePreference) => {
            if (
              !isNil(get(sitePreference, 'preferences.back_office.warehouse'))
            ) {
              return {
                ...result,
                [sitePreference.site_id]: get(
                  sitePreference,
                  'preferences.back_office.warehouse.primary_warehouse',
                ),
              };
            }

            return result;
          },
          {},
        );

        const resultSites = filter(filteredByFromSite, (site) => {
          return isEqual(get(sitePrimaryWarehouseMap, site.id), siteFromId);
        }) as ISite[];

        return setPrimaryAndWarehouseTop(resultSites);
      }),
      rx.toArray(),
      rx.finalize(() => {
        this.uiActions.disableModalLoader();
      }),
    );
  }

  public printSheetsBtnClickHandler = async (): Promise<void> => {
    this.initTransferFromSource();
    this.transferFromDataSource?.refresh();
    this.companySitesList$.next([]);
    const result = await this.openPrintSheetModal();

    if (!result.confirmed) return;
    const { site_id, items } = result.value;
    try {
      // get getInventoryItemVenues and print
      await this.printInventoryItemVenues(
        find(this.companySitesList, { id: site_id }),
        items,
      );
    } catch (error) {
      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: this.i18nService.instant(
          I18n.ErrorMessages.CheckInternetAndTryAgain,
        ),
      });
    }
  };

  public async duplicateButtonClick(transfers: ITransferData[]): Promise<void> {
    this.duplicate(transfers);
  }

  public fetchInventoryItemGroup(
    siteIds: string[],
    siteFromId: string,
  ): Promise<IBaseEntityResponse<IInventoryItem>> {
    const fetchInventoryItemDefaultParams = {
      masterFilter: 'measures/transactions/transfer eq in(true)',
      filter:
        'measures/transactions/transfer eq in(true) and is_master eq false',
      select: [
        'code',
        'name',
        'measures',
        'thresholds',
        'site_id',
        'is_chargeable',
      ],
      top: MAX_PAGE_SIZE,
    };

    return this.dmDataService
      .getSiteGroupIdList(siteIds)
      .pipe(
        rx.mergeMap((groupIds: string[]) =>
          this.inventoryItemVenueService.batchFetchCurrentList(
            groupIds,
            fetchInventoryItemDefaultParams,
          ),
        ),
        rx.map(
          (
            inventoryItemResponse: IBaseEntityResponse<IInventoryItem>,
          ): IBaseEntityResponse<IInventoryItem> => {
            const fromInventoryItemGroupEntityIds = chain(
              inventoryItemResponse.items,
            )
              .filter((item) => isEqual(item.site_id, siteFromId))
              .map((item) => item.entity_id)
              .value();

            const fromAndToInventoryItemGroup = filter(
              inventoryItemResponse.items,
              (item) =>
                includes(fromInventoryItemGroupEntityIds, item.entity_id),
            );

            inventoryItemResponse.items = fromAndToInventoryItemGroup;
            inventoryItemResponse.total = size(fromAndToInventoryItemGroup);

            return inventoryItemResponse;
          },
        ),
        rx.first(),
      )
      .toPromise();
  }

  @wrapWithLoader(null, 0, true)
  public async printInventoryItemVenues(
    fromSite: ISite,
    toSites: ISite[],
  ): Promise<void> {
    this.inventoryItemGroupResponse = await this.fetchInventoryItemGroup(
      [...map(toSites, 'id'), fromSite.id],
      fromSite.id,
    );
    const pdfConfig = buildPrintSheetPdfConfig(
      async (
        definitions: IPdfDefinitions,
        bulkData: IPdfDefinitions['bulkOptions'],
      ) => {
        return this.getPrintSheetPdfData(bulkData, definitions?.options);
      },
      {
        data: map(
          sortBy(toSites, (item) => toLower(item.name)),
          (toSite: ISite) => ({
            ...toSite,
            fromSiteName: fromSite.name,
            fromSiteStoreNumber: fromSite.store_number,
            fromSiteId: fromSite.id,
          }),
        ),
      },
      this.i18nService,
    );
    await this.pdfGenerateServiceV2.printPdfAsync(pdfConfig);
  }

  public async getPrintSheetPdfData(
    baseData: IBaseEntityType,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    options: IPdfOptions = {},
  ): Promise<IBaseEntityType> {
    const document: IBaseEntityType = {
      site_name: truncationText(
        generateSpacesText(baseData.fromSiteName, baseData.fromSiteStoreNumber),
        48,
        this.summaryTitleFontSize,
      ),
      site_to_name: truncationText(
        generateSpacesText(baseData.name, baseData.store_number),
        50,
        this.summaryTitleFontSize,
      ),
      print_date: this.companyUtilService.convertUTCDateWithTimeZone(
        toDate().toISOString(),
        true,
        TIME_FORMATS.L,
      ),
    };
    // deal the inventoryItems data
    const siteIdTo = get(baseData, 'id');
    const mergedInventoryItems = this.getInventoryItemsBySiteToId(siteIdTo);
    set(document, 'inventory_items_list', mergedInventoryItems);

    return document;
  }

  public getInventoryItemsBySiteToId(siteIdTo: string): IInventoryItem[] {
    return map(
      filter(this.inventoryItemGroupResponse.items, (item) =>
        isEqual(item.site_id, siteIdTo),
      ),
      (item) => {
        return {
          ...item,
          par: this.getParString(item.thresholds, item.measures),
          measures: this.filterTransferUnitMeasures(item),
        };
      },
    );
  }

  public getParString(
    thresholds: Threshold[],
    measures: InventoryItemVenueMeasure[],
  ): string {
    const translatedEach = this.i18nService.translate(I18n.Common.Each);
    // this method logic is according to XVI-6440 TC
    if (isNil(thresholds) || isEmpty(thresholds)) {
      return '';
    }
    const defaultThreshold = find(thresholds, (threshold: Threshold) =>
      isEqual(threshold.type, 'default'),
    );
    if (isNil(defaultThreshold) || isNil(defaultThreshold.par_qty)) {
      return '';
    }
    const defaultMeasure = find(
      measures,
      (measure: InventoryItemVenueMeasure) =>
        isEqual(measure.entity_id, defaultThreshold.measure_entity_id),
    );
    if (isNil(defaultMeasure)) {
      return '';
    }
    const defaultMeasureText = generateUnitTextV2(defaultMeasure, {
      numberFormatService: this.numberFormatService,
      measureTransactionType: MEASURE_TRANSACTION.Transfer,
      notNeedAddSign: true,
    });
    const prefixPar = `${this.numberFormatService.getNumberFormatter(false)({
      value: defaultThreshold.par_qty,
    })} ${defaultMeasureText}`;
    if (isEqual(defaultMeasure?.factor_value, 1)) {
      return prefixPar;
    }
    const measuresFactorValueIsOne = filter(
      measures,
      (measure: InventoryItemVenueMeasure) => isEqual(measure?.factor_value, 1),
    );
    const measuresDescriptionEach = find(
      measures,
      (measure: InventoryItemVenueMeasure) =>
        isEqual(measure.description, 'Each'),
    );
    const convertedValue =
      (defaultThreshold.par_qty * defaultMeasure.factor_value) /
      (measuresDescriptionEach?.factor_value || 1);
    if (isEmpty(measuresFactorValueIsOne) && isNil(measuresDescriptionEach)) {
      return `${prefixPar}/${this.numberFormatService.getNumberFormatter(false)(
        {
          value: convertedValue,
        },
      )} ${translatedEach}[1]`;
    }
    if (isEmpty(measuresFactorValueIsOne) && !isNil(measuresDescriptionEach)) {
      return isEqual(
        `${prefixPar}`,
        `${this.numberFormatService.getNumberFormatter(false)({
          value: defaultThreshold.par_qty,
        })} ${translatedEach}[${measuresDescriptionEach?.factor_value}]`,
      )
        ? `${prefixPar}`
        : `${prefixPar}/${this.numberFormatService.getNumberFormatter(false)({
            value: convertedValue,
          })} ${translatedEach}[${measuresDescriptionEach?.factor_value}]`;
    }
    if (!isEmpty(measuresFactorValueIsOne)) {
      const measureValueDescription: string[] = map(
        measuresFactorValueIsOne,
        (measureValue) =>
          `/${this.numberFormatService.getNumberFormatter(false)({
            value: defaultThreshold.par_qty * defaultMeasure?.factor_value,
          })} ${measureValue.description}[1]`,
      );

      return isEqual(`/${prefixPar}`, measureValueDescription.join(''))
        ? `${prefixPar}`
        : `${prefixPar}${measureValueDescription.join('')}`;
    }
  }

  public processNewStatusDocument(doc: ITransfer): ITransfer {
    if (!includes(TRANSFER_UNSAVED_STATUS, doc.status)) {
      if (!isEqual(TRANSFER_STATUS_VALUE.Completed, doc.curStatus)) {
        // remove unnecessary transfer_type
        delete doc['transfer_type'];
      }

      return doc;
    }
    doc.status = TRANSFER_STATUS_VALUE.Open;

    return doc;
  }

  public processCancelStatusDocument(doc: ITransfer): IBaseEntityType {
    if (doc.status !== TRANSFER_STATUS_VALUE.Canceled) return doc;

    return {
      _id: doc._id,
      status: doc.status,
    };
  }

  public processTransactionDateValue(doc: ITransfer): ITransfer {
    const transactionDateValue =
      get(doc, 'virtual.transaction_date') || doc.transaction_date;

    return {
      ...doc,
      transaction_date: transactionDateValue,
    };
  }

  public processDocWithDMStructure(doc: ITransfer): ITransfer {
    if (!has(doc, 'inventory_items')) return doc;
    // only use this to build item when status is new / open
    if (isXVIStructureDoc(doc.transfer_type, doc.status)) {
      if (
        !isEqual(TRANSFER_TYPE.PRE_EVENT_TRANSFER, doc.transfer_type) ||
        doc.status !== TRANSFER_STATUS_VALUE.Rejected
      ) {
        return doc;
      }
    }
    const flattenInventoryItemVenues = map(
      flatTreeDataByHasChildren(get(doc, 'inventory_items', [])),
      (item: IGridFlatInventoryItemVenue) => {
        const transferMeasure = find(
          item.measures,
          (measure) =>
            measure.transactions.transfer &&
            measure.entity_id === item?.measure_entity_id,
        );

        let countValue = {};

        if (!isEqual(TRANSFER_STATUS_VALUE.Completed, doc.curStatus)) {
          countValue = getIsShowReceivedColStatus(doc.transfer_type, doc.status)
            ? { count: parseFloat(`${get(item, 'count', null)}`) }
            : countValue;
        }

        return merge(
          {},
          {
            initial_count: parseFloat(`${get(item, 'initial_count', null)}`),
            _id: get(item, '_id') || generateObjectId(),
            gl_account_entity_id: get(item, 'gl_account.entity_id'),
            gl_account_name: get(item, 'gl_account.name'),
            item_entity_id: get(item, 'entity_id', get(item, 'item_entity_id')),
            item_name: get(item, 'name'),
            major_category_entity_id: get(
              item,
              COMMON_FORM_PATH.reportingCategoryMajorEntityId,
            ),
            major_category_name: get(
              item,
              COMMON_FORM_PATH.reportingCategoryMajorName,
            ),
            major_category_external_id: get(
              item,
              COMMON_FORM_PATH.reportingCategoryMajorExCategoryId,
              undefined,
            ),
            minor_category_entity_id: get(
              item,
              COMMON_FORM_PATH.reportingCategoryMinorEntityId,
            ),
            minor_category_name: get(
              item,
              COMMON_FORM_PATH.reportingCategoryMinorName,
            ),
            minor_category_external_id: get(
              item,
              COMMON_FORM_PATH.reportingCategoryMinorExCategoryId,
              undefined,
            ),
            measure_code: get(transferMeasure, 'code'),
            measure_description: get(transferMeasure, 'description'),
            measure_entity_id: get(
              transferMeasure,
              'entity_id',
              item?.measure_entity_id,
            ),
            measure_factor_value: get(transferMeasure, 'factor_value'),
            measure_id: get(transferMeasure, 'id'),
          },
          countValue,
        );
      },
    );
    set(
      doc,
      'inventory_items',
      filter(
        flattenInventoryItemVenues,
        (item: IGridFlatInventoryItemVenue) => item.count || item.initial_count,
      ),
    );

    return doc;
  }

  public processDocWithXVIStructure(doc: ITransfer): ITransfer {
    if (!has(doc, 'inventory_items')) return doc;
    if (!isXVIStructureDoc(doc.transfer_type, doc.status)) {
      return doc;
    }

    const flattenInventoryItemVenues = map(
      flatTreeDataByHasChildren(get(doc, 'inventory_items', [])),
      (item: IGridFlatInventoryItemVenue) => ({
        ...pick(item, apiItemFields),
        initial_count: isNil(item.initial_count)
          ? null
          : parseFloat(String(item.initial_count)),
        count: isNil(item.count) ? null : parseFloat(String(item.count)),
      }),
    );

    set(
      doc,
      'inventory_items',
      filter(
        flattenInventoryItemVenues,
        (item: IGridFlatInventoryItemVenue) => item.count || item.initial_count,
      ),
    );

    return doc;
  }

  public backToFrontItemMapper(doc: ITransfer): IBaseEntityType {
    set(doc, 'virtual.inventory_items', get(doc, 'inventory_items'));
    set(
      doc,
      'site_department_code_to',
      get(doc, 'site_department_code_to', '') || DEPARTMENT_CODE_NOT_CONFIGURED,
    );
    set(
      doc,
      'source',
      dataSourceValueFormatter(doc.source, { i18nService: this.i18nService }),
    );

    delete doc.inventory_items;

    return flow(
      this.markDocInventoryItemUpdated.bind(this),
      super.backToFrontItemMapper.bind(this),
    )(doc);
  }

  public frontToBackItemMapper(doc: ITransfer): IBaseEntityType {
    return flow(
      this.processDocWithDMStructure.bind(this),
      this.processDocWithXVIStructure.bind(this),
      this.processNewStatusDocument.bind(this),
      this.processCancelStatusDocument.bind(this),
      this.processTransactionDateValue.bind(this),
      (document) => {
        // remove local only fields
        unset(document, 'curStatus');
        // remove read-only fields
        unset(document, 'source');

        return document;
      },
      super.frontToBackItemMapper.bind(this),
    )(doc);
  }

  public async fetchGetDocumentById(
    documentId: string,
    options: IDocumentOperationOptions = {},
  ): Promise<ITransfer> {
    return this.apiActions.fetchGetDocumentById(
      transferEntityType,
      documentId,
      {
        ...options,
        query: {
          get_updated_items: true,
          ...options.query,
        },
      },
    );
  }

  public fetchList(
    userQuery?: IFilterQuery,
    options: IDocumentOperationOptions = {},
    skipRequestEvent: boolean = false,
    isTrowError: boolean = false,
  ): Observable<any> {
    const headers = this.composeHeadersForUpdateRequest({
      ...options,
      disallowBusinessDateHeaders: true,
    });
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    const params: HttpParams = this.createParams({
      top: MAX_PAGE_SIZE,
      ...query,
    });

    return this.http.get(this.getListBaseUrl(), { params, headers }).pipe(
      this.catchHttpError(options.errorOverride, false, false, {
        throwError: isTrowError,
      }),
      rx.switchMap((res: IBaseEntityResponse) => {
        const mergedEventTransfers$ =
          skipRequestEvent || isEmpty(get(res, 'items'))
            ? observableOf(res)
            : this.xprtDataService.combineEventIntoEntityResponse(res);

        return this.mergeValidateChange(res, mergedEventTransfers$, options);
      }),
      rx.map((res: IBaseEntityResponse) =>
        this.backToFrontMapper(res, options),
      ),
    );
  }

  public batchFetchList(
    userQuery?: IFilterQuery,
    siteGroupId: ObjectId = null,
  ): Observable<IBaseEntityResponse<ITransfer>> {
    const defaultPageSize = MAX_PAGE_SIZE;

    const buildParams = (
      page: number,
    ): [IFilterQuery, IDocumentOperationOptions, boolean] => [
      { ...userQuery, top: defaultPageSize, skip: page * defaultPageSize },
      {
        headers: {
          [EHeaderName.XSiteGroupId]: siteGroupId,
        },
        skipMappers: true,
      },
      true,
    ];

    const buildPageRange = (total: number): number[] => {
      return range(1, ceil(total / defaultPageSize));
    };

    return this.batchFetchDataHandler(
      this.fetchList,
      buildParams,
      buildPageRange,
    );
  }

  public mergeValidateChange(
    transferRes: IBaseEntityResponse,
    mergedEventTransfers$: Observable<IBaseEntityResponse>,
    options: IDocumentOperationOptions = {},
  ): Observable<IBaseEntityResponse> {
    const allCompletedTransferIds = reduce(
      transferRes.items,
      (pre, cur) => {
        if (isEqual(cur.status, TRANSFER_STATUS_VALUE.Completed)) {
          pre.push(cur._id);
        }

        return pre;
      },
      [],
    );
    const validateChange$ =
      !isEmpty(allCompletedTransferIds) &&
      includes(options.additionalFetch, 'validate-change') &&
      this.hasVoidPermission() &&
      this.hasManagePermission()
        ? this.validateChange({
            _id: allCompletedTransferIds,
            _change: {
              status: TRANSFER_STATUS_VALUE.Void,
            },
          })
        : observableOf<EntityValidateResponse>(null);

    return observableCombineLatest([
      mergedEventTransfers$,
      validateChange$,
    ]).pipe(
      rx.map(([mergedEventRes, validateRes]) => {
        if (isNil(validateRes) || isEmpty(validateRes)) {
          return mergedEventRes;
        }

        forEach(validateRes, (voidValidate) => {
          const matchItem = find(mergedEventRes?.items, {
            _id: voidValidate._id,
          });

          if (!isNil(matchItem)) {
            matchItem.visibleVoidBtn = !!voidValidate.allowed;
          }
        });

        return mergedEventRes;
      }),
    );
  }

  public markDocInventoryItemUpdated(doc: IBaseEntityType): IBaseEntityType {
    if (
      includes(TRANSFER_NOT_FINAL_STATUS, doc.status) &&
      !isNil(doc?.virtual?.inventory_items)
    ) {
      const updatedItems = get(doc, 'updated_items');
      set(
        doc,
        'virtual.inventory_items',
        markInventoryItemIsUpdated(doc.virtual.inventory_items, updatedItems),
      );
    }

    if (doc.transfer_type === TRANSFER_TYPE.DURING_EVENT_TRANSFER) {
      set(doc, 'virtual.transaction_date', get(doc, 'transaction_date'));
    }

    return doc;
  }

  public getQuantityOnHandData(
    inventoryItems: IQohInventoryItemVenue[],
    siteId: string,
    siteIdTo: string,
    entityId?: string,
  ): Observable<IQuantityOnHandResponse> {
    const fromSiteId = this.getDisplayQohFrom(siteId) ? siteId : '';
    const qohRequestData: IQuantityOnHandItem[] = map(
      compact([fromSiteId, siteIdTo]),
      (id) => ({
        site_id: id,
        inventory_item_entity_ids: map(
          inventoryItems,
          entityId || 'item_entity_id',
        ),
      }),
    );

    return this.qohService.batchFetchQuantityOnHand(qohRequestData);
  }

  public bulkVoid(
    selectedItems: ITransferData[],
    options?: {
      taskOptions?: ITaskOptions;
    },
  ): Observable<IBulkUpdateDocumentsResult> {
    const totalCount = size(selectedItems);

    const allFailedRes = {
      totalCount,
      successCount: 0,
    };

    if (lte(totalCount, 0)) {
      return observableOf(allFailedRes);
    }

    const { isSync, ...restTaskOptions } = merge(
      { isSync: false, noLoader: false, currentStep: 1, stepCount: 1 },
      get(options, 'taskOptions'),
    );

    return this.taskService
      .getBulkChangeStatusResult(
        {
          isSync,
          transferIds: map(selectedItems, '_id'),
          toStatus: TRANSFER_STATUS_VALUE.Void,
        },
        { isSync, ...restTaskOptions },
      )
      .pipe(
        rx.map((task: ITask) => {
          const updatedItems = chain(get(task, 'sub_tasks', []))
            .filter({ type: TASK_TYPE.UpdateTransfer })
            .map((item) => ({
              _id: get(item, 'value.transfer_id'),
              status: get(item, 'value.transfer_status'),
            }))
            .value();

          return <IBulkUpdateDocumentsResult>{
            totalCount,
            successCount: updatedItems.filter(
              (item) => item.status === TRANSFER_STATUS_VALUE.Void,
            ).length,
            updated_documents: updatedItems,
          };
        }),
        rx.catchError(() => observableOf(allFailedRes)),
      );
  }

  public async bulkVoidHandler(
    selectedItems: ITransferData[],
    listComp?: XUXListContainerComponent,
  ): Promise<IBulkUpdateDocumentsResult> {
    if (isEmpty(selectedItems)) {
      return;
    }

    const isSingleVoid = isEqual(size(selectedItems), 1);

    const totalCount = size(selectedItems);

    // need confirm message change: BULK_VOID_TRANSFER_CONFIRM_MESSAGE
    const confirmText = isSingleVoid
      ? this.i18nService.instant(I18n.InfoMessages.VoidAdverselyImpactQOH, {
          target: this.i18nService.translate(
            I18n.Forms.TransferForm.ThisTransfer,
          ),
        })
      : this.i18nService.instant(
          I18n.InfoMessages.TransferGroupVoidAdverselyImpactQOH,
          {
            count: totalCount,
            target: this.i18nService.translate(
              I18n.Forms.TransferForm.transfersBrackets,
            ),
          },
        );

    const modalOptions: IModalDialogOptions = {
      title: I18n.Common.Confirm,
      text: confirmText,
      buttons: [
        {
          ...BTN_YES,
          class: BTN_PRIMARY_CLASS,
        },
        BTN_CANCEL,
      ],
    };
    const result = await this.uiActions.promptFormModal(modalOptions);
    if (!result.confirmed) return;

    const bulkUpdateResult: IBulkUpdateDocumentsResult = await this.bulkVoid(
      selectedItems,
      {
        taskOptions: {
          isSync: isSingleVoid,
        },
      },
    ).toPromise();

    if (isNil(bulkUpdateResult)) {
      return;
    }

    const successCount = get(bulkUpdateResult, 'successCount', 0);
    const failedCount = get(bulkUpdateResult, 'totalCount', 0) - successCount;

    if (failedCount > 0) {
      let errorMsg = isSingleVoid
        ? this.i18nService.instant(I18n.ErrorMessages.SingleTransferVoidFailed)
        : this.i18nService.instant(
            I18n.ErrorMessages.PartialTransferVoidFailed,
            {
              failedCount,
            },
          );

      if (successCount > 0) {
        errorMsg =
          this.i18nService.instant(I18n.InfoMessages.PartialOrAllsuccessfully, {
            successCount,
            target: this.i18nService.translate(
              I18n.Forms.TransferForm.transfersBrackets,
            ),
            did: this.i18nService.translate(
              TRANSFER_OPERATION_LOWER_STATUS_MAPPING[
                TRANSFER_STATUS_VALUE.Void
              ],
            ),
            trailSymbol: '. ',
          }) + errorMsg;
      }

      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: errorMsg,
      });
    } else {
      this.uiActions.showToasterMessage({
        type: NotificationType.SUCCESS,
        title: TOASTER_SUCCESS_MESSAGE.title,
        body: isSingleVoid
          ? this.i18nService.translate(I18n.InfoMessages.Didsuccessfully, {
              did: this.i18nService.translate(
                TRANSFER_OPERATION_STATUS_MAPPING[TRANSFER_STATUS_VALUE.Void],
              ),
            })
          : this.i18nService.instant(
              I18n.InfoMessages.PartialOrAllsuccessfully,
              {
                successCount,
                target: this.i18nService.translate(
                  I18n.Forms.TransferForm.transfersBrackets,
                ),
                did: this.i18nService.translate(
                  TRANSFER_OPERATION_LOWER_STATUS_MAPPING[
                    TRANSFER_STATUS_VALUE.Void
                  ],
                ),
                trailSymbol: '!',
              },
            ),
      });
    }

    // refresh list
    listComp?.refresh();

    return bulkUpdateResult;
  }

  public bulkVoidHidden(selectedItems: IBaseEntityType[]): boolean {
    // must have transfer permission if shown this button
    if (!(this.hasManagePermission() && this.hasVoidPermission())) {
      return true;
    }

    const preEventOrNotCompletedItem = find(
      selectedItems,
      (selectedItem) =>
        isEqual(TRANSFER_TYPE.PRE_EVENT_TRANSFER, selectedItem.transfer_type) ||
        !isEqual(TRANSFER_STATUS_VALUE.Completed, selectedItem.status),
    );

    return isEmpty(selectedItems) || !!preEventOrNotCompletedItem;
  }

  public hasManagePermission(): boolean {
    return this.permissionService.canManage(ENTITY_TYPES.TRANSFER);
  }

  public hasVoidPermission(): boolean {
    return this.permissionService.canOperateByCode(
      ENTITY_TYPES.TRANSFER,
      'Void',
    );
  }

  /**
   * @deprecated should use takeIntersectionAndProcessThreshold in the replenish utils instead
   */
  public takeIntersectionAndProcessThreshold(
    res: IBaseEntityResponse<InventoryItemVenue>,
    siteIdTo: string,
  ): IBaseEntityResponse<InventoryItemVenue> {
    const entityIdDictionary = countBy(res.items, 'entity_id');

    const deDuplicateAndThresholdItems = filter(
      res.items,
      (item: IQohInventoryItemVenue) => {
        return (
          entityIdDictionary[item.entity_id] > 1 &&
          isEqual(item.site_id, siteIdTo)
        );
      },
    );

    return <IBaseEntityResponse<InventoryItemVenue>>{
      ...res,
      items: deDuplicateAndThresholdItems,
      total: deDuplicateAndThresholdItems.length,
    };
  }

  public filterTransferUnitMeasures(
    inventoryItem: IInventoryItem,
  ): InventoryItemVenueMeasure[] {
    const measures =
      map(inventoryItem.measures, (measure) => {
        measure.value = measure.entity_id;

        return measure;
      }) || [];
    const transferMeasures = filter(
      measures,
      (item) => item.transactions.transfer,
    );
    // sort the transfer Unit by description
    const sortMeasures = sortBy(transferMeasures, (item) =>
      toLower(item.description),
    );

    return sortMeasures;
  }

  public generateUnitCostMapper(inventoryItem: IInventoryItem): Object {
    const unitCostMapper = {};
    map(inventoryItem.measures, (singleMeasure) => {
      const costsParam = find(inventoryItem.costs, 'is_preferred');
      unitCostMapper[singleMeasure.entity_id] = decimalNumber(
        get(costsParam, 'cost_value', 0),
      ).mul(get(singleMeasure, 'factor_value', 0));
    });

    return unitCostMapper;
  }

  public async prepareItemsDataForWrittenSheet(
    currentDocument,
  ): Promise<IInventoryItem[]> {
    const siteId = get(currentDocument, 'site_id');
    const siteIdTo = get(currentDocument, 'site_id_to');

    const allItemsResponse = await this.inventoryItemVenueService
      .fetchCurrentList(
        {
          filter: `measures/transactions/transfer eq in(true)`,
          select: [
            'measures',
            'name',
            'gl_account',
            'reporting_category_major',
            'reporting_category_minor',
            'costs',
            'is_chargeable',
          ],
        },
        [siteId, siteIdTo],
      )
      .toPromise();

    const data: IBaseEntityResponse<IInventoryItem> =
      this.takeIntersectionAndProcessThreshold(allItemsResponse, siteIdTo);

    return data.items;
  }

  public getDisplayQohFrom(siteId: string, sites?: ISite[]): boolean {
    const displayType = this.stateService.getTransferDisplayQohFrom();
    const site =
      find(!isEmpty(sites) ? sites : this.companySitesList, (item) =>
        isEqual(item.id, siteId),
      ) || ({} as ISite);
    if (!isSiteTypeWarehouse(site)) {
      return true;
    }
    if (!displayType && isSiteTypeWarehouse(site)) {
      return false;
    }

    return true;
  }

  public dealQohFromParams(
    params: IQuantityOnHandItem[],
  ): IQuantityOnHandItem[] {
    // when DM config qoh from display is off we not need get from site qoh
    if (
      isEqual(size(params), 2) &&
      !this.getDisplayQohFrom(params[1].site_id)
    ) {
      return [first(params)];
    }

    return params;
  }

  public getBulkSitesDisplayQoh(siteIds: string[]): boolean {
    const selectedFromSites = filter(this.companySitesList, (item: ISite) =>
      includes(siteIds, item.id),
    ) as ISite[];
    // get DM preferences QOH show or hidden, hidden should filter the QOH column
    const displayType = this.stateService.getTransferDisplayQohFrom();
    if (
      !displayType &&
      every(selectedFromSites, (selectedFromSite) =>
        isSiteTypeWarehouse(selectedFromSite),
      )
    ) {
      // hide QOH column
      return false;
    }

    return true;
  }

  public async getPdfData(
    transfer: IBaseEntityType,
    options: IPdfOptions = {},
    dataOptions?: {
      showQohFromOverride?: boolean;
      isGetUpdatedItems?: boolean;
    },
  ): Promise<IBaseEntityType> {
    const { showQohFromOverride, isGetUpdatedItems } = merge(
      {
        showQohFromOverride: null,
        isGetUpdatedItems: null,
      },
      dataOptions,
    );

    const transferStatus = get(transfer, 'status', TRANSFER_STATUS_VALUE.Open);
    const currentDocument = await this.getDocumentByIdWithSkipUpdateDocument(
      transfer._id,
      {
        noLoader: true,
        query: {
          get_updated_items: isNil(isGetUpdatedItems)
            ? includes(this.needGetUpdateItemsStatus, transferStatus)
            : isGetUpdatedItems,
        },
        suppressErrors: true,
      },
    );
    this.events = await this.transferPdfService.getEvents();
    const currentEvent = find(this.events, { id: currentDocument.event_id });
    const transferType = get(currentDocument, 'transfer_type');

    if (has(currentDocument, 'errors')) {
      return Promise.reject(
        this.i18nService.instant(I18n.ErrorMessages.CheckInternetAndTryAgain),
      );
    }
    set(
      currentDocument,
      TRANSFER_FORM_FIELD_PATHS.transferGroupName,
      get(transfer, TRANSFER_FORM_FIELD_PATHS.transferGroupName, ''),
    );
    set(
      currentDocument,
      TRANSFER_FORM_FIELD_PATHS.eventId,
      find(this.events, { id: currentDocument.event_id })?.name,
    );

    const isOpenOrPending = includes(
      [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.Pending],
      transferStatus,
    );
    set(
      currentDocument,
      'displayQoh',
      !isNil(showQohFromOverride)
        ? showQohFromOverride
        : this.getDisplayQohFrom(
            get(currentDocument, TRANSFER_FORM_FIELD_PATHS.siteId, ''),
          ),
    );
    const inventoryItems = get(
      currentDocument,
      TRANSFER_FORM_FIELD_PATHS.virtualInventoryItemVenues,
      [],
    );
    if (isOpenOrPending) {
      const siteId = get(currentDocument, 'site_id');
      const siteIdTo = get(currentDocument, 'site_id_to');
      const query = {
        filter: 'measures/transactions/transfer eq in(true)',
        select: [
          'code',
          'reporting_category_major',
          'reporting_category_minor',
          'name',
          'gl_account',
          'measures',
          'vendors',
          'thresholds',
          'costs',
          'is_chargeable',
        ],
      };
      const mergedInventoryItems = await this.inventoryItemVenueService
        .fetchCurrentList(query, [siteId, siteIdTo])
        .pipe(
          rx.map((res: IBaseEntityResponse) => {
            return this.getInventoryItemsEntityId(
              res,
              inventoryItems,
              currentEvent,
              transferType,
            );
          }),
          rx.map((res: IBaseEntityResponse<InventoryItemVenue>) =>
            this.takeIntersectionAndProcessThreshold(res, siteIdTo),
          ),
          rx.map((res: IBaseEntityResponse) =>
            this.mixinXVIData(
              res.items as InventoryItemVenue[],
              inventoryItems,
            ),
          ),
          rx.map((res) => {
            return this.addCountAndErrorToItems(res, inventoryItems);
          }),
          rx.map((res) => {
            const unsavedData = get(
              transfer,
              TRANSFER_FORM_FIELD_PATHS.virtualReallyChangedTransfers,
            );

            if (!isNil(get(unsavedData, transfer.site_id_to))) {
              forEach(unsavedData[transfer.site_id_to].items, (unSavedItem) => {
                const targetUnsavedItem = find(res, {
                  entity_id: unSavedItem.entity_id,
                });

                if (!isNil(targetUnsavedItem)) {
                  set(
                    targetUnsavedItem,
                    'initial_count',
                    get(
                      unSavedItem,
                      'initial_count',
                      targetUnsavedItem.initial_count,
                    ),
                  );
                  set(
                    targetUnsavedItem,
                    'count',
                    get(unSavedItem, 'count', targetUnsavedItem.count),
                  );
                  set(
                    targetUnsavedItem,
                    'measure_entity_id',
                    get(
                      unSavedItem,
                      'measure_entity_id',
                      targetUnsavedItem.measure_entity_id,
                    ),
                  );
                }
              });
            }

            return res;
          }),
          rx.map((res: IQohInventoryItemVenue[]) => {
            return appendInitialFactorAndCountValue(res);
          }),
          rx.mergeMap((iQohInventoryItems: IQohInventoryItemVenue[]) =>
            iif(
              () => isEmpty(iQohInventoryItems),
              of([]),
              this.getQuantityOnHandData(
                iQohInventoryItems,
                getQohSiteIdFieldByOverride(showQohFromOverride, siteId),
                siteIdTo,
                ENTITY_ID_PATH,
              ).pipe(
                rx.map((qohRes) => {
                  return {
                    dmInventoryItems: iQohInventoryItems,
                    qohRes: qohRes.items,
                  };
                }),
                rx.map((inventoryItemsWithQoh) => {
                  this.transferPdfService.setQOHToDocument(
                    inventoryItemsWithQoh.dmInventoryItems,
                    inventoryItemsWithQoh.qohRes,
                    siteId,
                    siteIdTo,
                  );

                  return inventoryItemsWithQoh.dmInventoryItems;
                }),
                rx.mergeMap((dmInventoryItems) => {
                  return this.getPendingQuantity(
                    transferStatus,
                    transfer._id,
                    siteId,
                    siteIdTo,
                  ).pipe(
                    rx.map((pendingQtyRes) => {
                      return { dmInventoryItems, pendingQty: pendingQtyRes };
                    }),
                  );
                }),
                rx.map((inventoryItemsWithPendingQoh) => {
                  if (
                    isEqual(TRANSFER_STATUS_VALUE.Pending, transferStatus) ||
                    isEqual(TRANSFER_STATUS_VALUE.Open, transferStatus)
                  ) {
                    const pendingQtyMap =
                      this.transferPdfService.createPendingQuantityMap(
                        inventoryItemsWithPendingQoh.pendingQty,
                        siteId,
                        siteIdTo,
                      );
                    this.transferPdfService.setPendingQtyToDocument(
                      inventoryItemsWithPendingQoh.dmInventoryItems,
                      pendingQtyMap,
                    );
                  }

                  return inventoryItemsWithPendingQoh.dmInventoryItems;
                }),
                rx.map((dmInventoryItems) => {
                  const isOutsideOfEventTransfer = isEqual(
                    TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER,
                    transferType,
                  );
                  this.transferPdfService.appendExtraInfo(
                    dmInventoryItems,
                    currentEvent,
                    isOutsideOfEventTransfer,
                  );

                  return dmInventoryItems;
                }),
              ),
            ),
          ),
        )
        .toPromise();

      set(
        currentDocument,
        TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues,
        mergedInventoryItems,
      );
    } else {
      forEach(inventoryItems, (item) => {
        set(item, 'gl_account.name', get(item, 'gl_account_name'));
      });
      set(
        currentDocument,
        TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues,
        inventoryItems,
      );
    }

    return this.transferPdfService.getPdfData(
      merge({}, currentDocument, omit(transfer, 'event_id')),
      {
        xuxGrid: null,
        isFromDetail: false,
        docEvent: currentEvent,
      },
      options,
    );
  }

  public async getPdfConfig(
    dataFn: Function,
    modalColumns: PdfSelectColumn[],
    bulkOptions?: IPdfBulkOptions,
    specialOptions?: IPdfSpecialOptions,
  ): Promise<IPdfDefinitions> {
    const pdfConfig = this.pdfConfigBuilder(dataFn, modalColumns, bulkOptions, {
      ...specialOptions,
      i18nService: this.i18nService,
    });

    return pdfConfig;
  }

  public async getWrittenSheetPdfConfig(
    dataFn: Function,
    isShowAllColumns: boolean,
    bulkOptions?: IPdfBulkOptions,
    specialOptions?: IPdfSpecialOptions,
  ): Promise<IPdfDefinitions> {
    const pdfWrittenSheetConfigBuilder = this.pdfWrittenSheetConfigBuilder(
      dataFn,
      isShowAllColumns,
      bulkOptions,
      specialOptions,
    );

    return pdfWrittenSheetConfigBuilder;
  }

  public async getWrittenSheetPdfData(
    transfer: IBaseEntityType,
    options: IPdfOptions = {},
    dataOptions?: {
      showQohFromOverride?: boolean;
      isGetUpdatedItems?: boolean;
      noLoader?: boolean;
    },
  ): Promise<IBaseEntityType> {
    const { showQohFromOverride, isGetUpdatedItems, noLoader } = merge(
      {
        showQohFromOverride: null,
        isGetUpdatedItems: null,
        noLoader: false,
      },
      dataOptions,
    );

    const transferStatus = get(transfer, 'status', TRANSFER_STATUS_VALUE.Open);
    const currentDocument = await this.getDocumentByIdWithSkipUpdateDocument(
      transfer._id,
      {
        noLoader,
        query: {
          get_updated_items: isNil(isGetUpdatedItems)
            ? includes(this.needGetUpdateItemsStatus, transferStatus)
            : isGetUpdatedItems,
        },
        suppressErrors: true,
      },
    );

    const siteId = get(currentDocument, 'site_id');
    const siteIdTo = get(currentDocument, 'site_id_to');
    set(
      currentDocument,
      'displayQoh',
      !isNil(showQohFromOverride)
        ? showQohFromOverride
        : this.getDisplayQohFrom(siteId),
    );
    const inventoryItems =
      await this.prepareItemsDataForWrittenSheet(currentDocument);

    const unsavedData = get(
      transfer,
      TRANSFER_FORM_FIELD_PATHS.virtualReallyChangedTransfers,
    );

    const transferItemList = get(currentDocument, 'virtual.inventory_items');

    const mergedItemData = map(inventoryItems, (item: InventoryItemVenue) => {
      const targetTransferItem = find(
        transferItemList,
        (transferItem) => transferItem.item_entity_id === item.entity_id,
      );

      const targetUnsavedChangedItem = !isNil(
        get(unsavedData, transfer.site_id_to),
      )
        ? find(unsavedData[transfer.site_id_to].items, {
            entity_id: item.entity_id,
          })
        : {};

      return <IGridFlatInventoryItemVenue>{
        ...targetTransferItem,
        ...targetUnsavedChangedItem,
        measures: this.filterTransferUnitMeasures(item),
        base_measure_count: targetTransferItem
          ? targetTransferItem.base_measure_count
          : null,
        gl_account_entity_id: item.gl_account.entity_id,
        'gl_account.name': item.gl_account.name,
        item_cost: targetTransferItem ? targetTransferItem.item_cost : null,
        item_name: item.name,
        major_category_name: item.reporting_category_major.name,
        major_category_entity_id: item.reporting_category_major.entity_id,
        minor_category_name: item.reporting_category_minor.name,
        minor_category_entity_id: item.reporting_category_minor.entity_id,
        total_cost: targetTransferItem ? targetTransferItem.total_cost : null,
        item_entity_id: item.entity_id,
        item_cost_mapper: this.generateUnitCostMapper(item),
        costs: get(item, 'costs'),
        entity_id: get(item, 'entity_id'),
        is_chargeable: get(item, 'is_chargeable', false),
      };
    });

    set(
      currentDocument,
      TRANSFER_FORM_FIELD_PATHS.virtualInventoryItemVenues,
      mergedItemData,
    );

    if (
      get(currentDocument, 'virtual.inventory_items.length') > 0 &&
      (isEqual(transferStatus, TRANSFER_STATUS_VALUE.Open) ||
        isEqual(transferStatus, TRANSFER_STATUS_VALUE.Pending))
    ) {
      // Get QOH
      const qohData = await this.getQuantityOnHandData(
        currentDocument.virtual.inventory_items,
        getQohSiteIdFieldByOverride(showQohFromOverride, siteId),
        siteIdTo,
      ).toPromise();
      this.transferPdfService.setQOHData(qohData.items);
    }

    return this.generateWrittenSheetPdfData(
      merge({}, currentDocument, omit(transfer, 'event_id')),
      false,
      null,
      options,
    );
  }

  public getVoidAllowedList(ids: string[]): Observable<EntityValidateResponse> {
    if (isEmpty(ids)) {
      return observableOf([]);
    }

    return this.validateChange({
      _id: ids,
      _change: {
        status: TRANSFER_STATUS_VALUE.Void,
      },
    });
  }

  public async generateWrittenSheetPdfData(
    doc: IBaseEntityType,
    isFromDetail?,
    editor?,
    options: IPdfOptions = {},
  ): Promise<IBaseEntityType> {
    const currentDocument = merge({}, doc);
    const transferStatus = get(
      currentDocument,
      'status',
      TRANSFER_STATUS_VALUE.Open,
    );
    const isOpenTransfer = isEqual(TRANSFER_STATUS_VALUE.Open, transferStatus);
    const gridFilterString = get(
      editor,
      'entityFormService.xuxGrid.xuxGridComponent.actionPanel.quickFilterControl.value',
    );

    set(currentDocument, 'gridFilterString', gridFilterString);
    set(currentDocument, 'isOpenTransfer', isOpenTransfer);

    this.events = await this.transferPdfService.getEvents();

    if (
      !isEmpty(this.events) &&
      includes(
        [TRANSFER_TYPE.DURING_EVENT_TRANSFER, TRANSFER_TYPE.PRE_EVENT_TRANSFER],
        currentDocument.transfer_type,
      )
    ) {
      currentDocument.event_name = find(this.events, {
        id: currentDocument.event_id,
      })?.name;
    }

    let inventoryItems = isFromDetail
      ? flatTreeDataByHasChildren<IGridFlatInventoryItemVenue>(
          get(currentDocument, TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues),
        )
      : get(
          currentDocument,
          TRANSFER_FORM_FIELD_PATHS.virtualInventoryItemVenues,
        );

    inventoryItems = map(inventoryItems, (item) => {
      const unitCost = item[isOpenTransfer ? 'item_cost' : 'initial_item_cost'];
      const totalCost = !isNil(item.initial_count)
        ? new DecimalNumber(item.initial_count).mul(unitCost)
        : null;

      return {
        ...item,
        total_cost: totalCost,
      } as unknown as IGridFlatInventoryItemVenue;
    });

    const filteredData: IGridFlatInventoryItemVenue[] = isFromDetail
      ? inventoryItems
      : get(
          currentDocument,
          TRANSFER_FORM_FIELD_PATHS.virtualInventoryItemVenues,
        );

    if (isFromDetail) {
      const fixedData = map(filteredData, (item: IInventoryItem) => {
        return {
          ...item,
          item_entity_id: item.entity_id,
          costs: item?.costs,
          base_measure_count: item.base_measure_count,
          count: item.count,
          gl_account_entity_id: item.gl_account.entity_id,
          'gl_account.name': item.gl_account.name,
          initial_count: item.initial_count,
          item_cost: item.item_cost,
          item_name: item.name,
          major_category_name: item.reporting_category_major.name,
          major_category_entity_id: item.reporting_category_major.entity_id,
          minor_category_name: item.reporting_category_minor.name,
          minor_category_entity_id: item.reporting_category_minor.entity_id,
          total_cost: item.total_cost,
          item_cost_mapper: this.generateUnitCostMapper(item),
          entity_id: item.entity_id,
        } as unknown as IGridFlatInventoryItemVenue;
      });

      set(
        currentDocument,
        TRANSFER_FORM_FIELD_PATHS.virtualInventoryItemVenues,
        fixedData,
      );
    }

    const qohData = this.transferPdfService.getQOHData();

    const items = this.transferPdfService.setQOHToDocument(
      currentDocument.virtual.inventory_items,
      qohData,
      currentDocument.site_id,
      currentDocument.site_id_to,
    );
    set(currentDocument, 'virtual.inventory_items', items);

    const noSkipEmptyValue = true;
    const includeDepartmentCodeRow = !!find(options.selectedColumns, (column) =>
      isEqual(column.title, I18n.EntityNames.DepartmentCode),
    );
    const totals = this.transferTotalFormService.mapItemDataToTotalsData(
      inventoryItems,
      TRANSFER_FORM_FIELD_PATHS,
      {
        status: currentDocument?.status,
        transfer_type: currentDocument?.transfer_type,
        site_department_code_to: currentDocument?.site_department_code_to,
      },
      noSkipEmptyValue,
      'initial_count',
      includeDepartmentCodeRow,
    );

    const pinnedBottomData = map(totals.pinnedBottomData, (bottomData) => ({
      ...bottomData,
      isPinnedBottom: true,
    }));
    const flatRows = this.transferPdfService.flatTotalRows(totals.normalRows);
    set(
      currentDocument,
      TRANSFER_FORM_FIELD_PATHS.virtualTotals,
      concat(flatRows, pinnedBottomData),
    );

    return currentDocument;
  }

  public getBulkUpdateStatusFn(
    action: string,
    did: string,
    taskFlow: boolean = false,
  ): Function {
    return async (
      selectedItems: ITransferData[],
      listComp: XUXListContainerComponent,
    ): Promise<void> => {
      const bulkUpdateFn = taskFlow
        ? this.taskFlowBulkUpdateStatus.bind(this)
        : this.processBulkUpdateStatus.bind(this);

      await bulkUpdateFn(action, did, selectedItems, listComp);

      return;
    };
  }

  public editTransfer(transfer: ITransferData): void {
    this.router.navigate([this.config.uiRoute, get(transfer, '_id', '')], {
      queryParams: {
        get_updated_items: false,
      },
    });
  }

  public async taskFlowBulkUpdateStatus(
    action: string,
    did: string,
    selectedItems: ITransferData[],
    listComp: XUXListContainerComponent,
  ): Promise<void> {
    if (isEmpty(selectedItems)) {
      return;
    }

    const needDmSync = includes(
      [COMMON_ACTION_TYPES.Submit, COMMON_ACTION_TYPES.Accept],
      action,
    );

    const saveResult =
      await this.transferBulkStatusService.processBulkUpdateStatus(
        selectedItems,
        {
          action,
          needDmSync,
          onSingleDmSync: needDmSync
            ? (transfer): void => this.editTransfer(transfer)
            : null,
        },
      );

    if (saveResult !== 'Cancel') {
      // refresh list
      listComp?.refresh();
    }
  }

  public async processBulkUpdateStatus(
    action: string,
    did: string,
    selectedItems: ITransferData[],
    listComp: XUXListContainerComponent,
  ): Promise<void> {
    if (isEmpty(selectedItems)) {
      return;
    }

    const result = await this.uiActions.confirm({
      text: this.i18nService.translate(
        I18n.ConfirmMessages.AreYouWantToBulkUpdateTarget,
        {
          target: this.i18nService.translate(
            I18n.Forms.TransferForm.TransfersBrackets,
          ),
          wantTo: this.i18nService.translate(action),
        },
      ),
    });
    if (!result.confirmed) return;

    const statusMapping = {
      submit: TRANSFER_STATUS_VALUE.Pending,
      cancel: TRANSFER_STATUS_VALUE.Canceled,
      accept: TRANSFER_STATUS_VALUE.Completed,
      reject: TRANSFER_STATUS_VALUE.Rejected,
    };
    const status = statusMapping[action];

    let needAutoComplete: boolean = false;
    if (isEqual(action, COMMON_ACTION_TYPES.Submit)) {
      needAutoComplete = some(selectedItems, (item) =>
        isOpenOrNewDuringAutoCompleteTransfer(
          item.transfer_type,
          item.status,
          this.stateService,
        ),
      );
    }
    let needAutoTransfers: ITransferData[] = [];
    let noNeedAutoTransfers: ITransferData[] = selectedItems;

    if (needAutoComplete) {
      [needAutoTransfers, noNeedAutoTransfers] = partition(
        selectedItems,
        (item) =>
          isEqual(TRANSFER_TYPE.DURING_EVENT_TRANSFER, item.transfer_type),
      );
    }

    await observableCombineLatest([
      this.bulkUpdateStatus(status, noNeedAutoTransfers),
      iif(
        () => needAutoComplete,
        this.bulkUpdateStatus(
          TRANSFER_STATUS_VALUE.Completed,
          needAutoTransfers,
        ),
        of({}),
      ),
    ])
      .pipe(
        rx.tap(
          ([noNeedAutoResult, needAutoResult]: [
            IMergeBulkUpdateDocumentsResult,
            IMergeBulkUpdateDocumentsResult,
          ]) => {
            if (isEmpty(needAutoResult)) {
              this.shownBulkUpdateMessage(status, did, noNeedAutoResult);
            } else {
              const { updateItemCount, needDmSyncItemCount, bulkUpdateResult } =
                noNeedAutoResult;
              const {
                updateItemCount: updateItemCountForAuto,
                needDmSyncItemCount: needDmSyncItemCountForAuto,
                bulkUpdateResult: bulkUpdateResultForAuto,
              } = needAutoResult;
              this.shownBulkUpdateMessage(status, did, {
                updateItemCount: updateItemCount + updateItemCountForAuto,
                needDmSyncItemCount:
                  needDmSyncItemCount + needDmSyncItemCountForAuto,
                bulkUpdateResult: {
                  successCount:
                    bulkUpdateResult.successCount +
                    bulkUpdateResultForAuto.successCount,
                  totalCount:
                    bulkUpdateResult.totalCount +
                    bulkUpdateResultForAuto.totalCount,
                },
              });
            }
          },
        ),
      )
      .toPromise();

    // refresh list
    listComp.refresh();
  }

  public bulkUpdateStatus(
    status: TRANSFER_STATUS_VALUE,
    selectedItems: ITransferData[],
  ): Observable<IMergeBulkUpdateDocumentsResult> {
    this.uiActions.enableLoader();

    const isNeedFilterDMSyncItems = includes(
      [
        TRANSFER_STATUS_VALUE.Pending,
        TRANSFER_STATUS_VALUE.Completed,
        TRANSFER_STATUS_VALUE.Rejected,
      ],
      status,
    );

    const isNeedCheckDmSync = includes(
      [
        TRANSFER_STATUS_VALUE.Pending,
        TRANSFER_STATUS_VALUE.Canceled,
        TRANSFER_STATUS_VALUE.Completed,
        TRANSFER_STATUS_VALUE.Rejected,
      ],
      status,
    );

    const totalCount = size(selectedItems);

    // filter need dm sync item now when is submit transfer
    const bulkUpdateItems = isNeedFilterDMSyncItems
      ? filter(selectedItems, (item) => !isNeedDMSync(item))
      : selectedItems;

    let updateItemCount = size(bulkUpdateItems);
    let needDmSyncItemCount = totalCount - updateItemCount;

    return this.bulkUpdateEntity(
      map(bulkUpdateItems, (validSelectedItem) => ({
        status,
        _id: validSelectedItem._id,
      })),
      {},
      BULK_REQUEST_COUNT,
      // need call detail api to confirm is data need dm sync when is need check dm sync
      isNeedCheckDmSync
        ? (doc: IBaseEntityType): Observable<boolean> =>
            this.getDocumentById(doc._id, {
              query: { get_updated_items: true },
            }).pipe(
              rx.switchMap((detailInfo) => {
                const needDMSync = isNeedDMSync(detailInfo);

                // need dm sync but is cancel transfer, just acknowledge it and continue to save it change
                if (
                  needDMSync &&
                  isEqual(TRANSFER_STATUS_VALUE.Canceled, status)
                ) {
                  return fromPromise(this.acknowledgeDocument(doc._id)).pipe(
                    rx.map(() => true),
                  );
                }

                // increase needDmSyncItemCount & decrease updateItemCount when data need dm sync
                if (needDMSync) {
                  needDmSyncItemCount = needDmSyncItemCount + 1;
                  updateItemCount = updateItemCount - 1;
                }

                return observableOf(!needDMSync);
              }),
              rx.catchError(() => observableOf(false)),
            )
        : null,
    ).pipe(
      rx.map((updateResult) => ({
        updateItemCount,
        needDmSyncItemCount,
        bulkUpdateResult: {
          ...updateResult,
          totalCount,
        },
      })),
      rx.finalize(() => {
        this.uiActions.disableLoader();
      }),
    );
  }

  public shownBulkUpdateMessage(
    status: TRANSFER_STATUS_VALUE,
    did: string,
    updateResult: IMergeBulkUpdateDocumentsResult,
  ): void {
    const { updateItemCount, needDmSyncItemCount, bulkUpdateResult } =
      updateResult;

    const failedCount =
      updateItemCount - get(bulkUpdateResult, 'successCount', 0);

    if (failedCount <= 0 && needDmSyncItemCount <= 0) {
      this.uiActions.showToasterMessage({
        type: NotificationType.SUCCESS,
        title: TOASTER_SUCCESS_MESSAGE.title,
        body: this.i18nService.translate(I18n.InfoMessages.Didsuccessfully, {
          did: this.i18nService.translate(STATUS_ACTION_MAPPING[status]),
        }),
      });
    }

    if (needDmSyncItemCount > 0) {
      const dmSyncDoingMsgMapping = {
        [TRANSFER_STATUS_VALUE.Pending]: I18n.Forms.GridForm.Submitting,
        [TRANSFER_STATUS_VALUE.Completed]: I18n.Forms.GridForm.Accepting,
        [TRANSFER_STATUS_VALUE.Rejected]: I18n.Forms.GridForm.Rejecting,
      };

      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: this.i18nService.translate(
          I18n.InfoMessages.TransfersNeedDMSyncBeforeDoing,
          {
            failedCount: needDmSyncItemCount,
            cannotDo: did,
            doing: this.i18nService.translate(dmSyncDoingMsgMapping[status]),
          },
        ),
      });
    }

    if (failedCount > 0) {
      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: this.i18nService.translate(
          I18n.ErrorMessages.PartialBulkFailedPlsTryAgain,
          {
            failedCount,
            did,
            target: this.i18nService.translate(
              I18n.Forms.TransferForm.transfersBrackets,
            ),
          },
        ),
      });
    }
  }

  public autoRestockHidden(): boolean {
    return !this.permissionService.canAdd(TransferService.ENTITY_TYPE);
  }

  public async openAutoRestock(): Promise<void> {
    this.uiActions.openRoute(this.transferAutoRestockUri);
  }

  public async getModalColumns(
    fromSiteId: string,
    modalColumns: IPdfTableHeader[],
    curStatus: string,
  ): Promise<IPdfTableHeader[]> {
    const selectedFromSite = find(
      this.companySitesList,
      (item: ISite) => item.id === fromSiteId,
    );
    let canSelectModalColumns = modalColumns;
    // get DM preferences QOH show or hidden, hidden should filter the QOH column
    const isDisplay = this.stateService.getTransferDisplayQohFrom();
    if (!isDisplay && isSiteTypeWarehouse(selectedFromSite)) {
      // hide QOH column
      canSelectModalColumns = filterQohFromColumn(canSelectModalColumns);
    }
    canSelectModalColumns = addParQtyColumn(canSelectModalColumns, curStatus);

    return canSelectModalColumns;
  }

  public getPreRejectTransfers(transfers: ITransferData[]): ITransferData[] {
    return map(cloneDeep(transfers), (transfer) => {
      if (transfer?.transfer_type === TRANSFER_TYPE.PRE_EVENT_TRANSFER) {
        set(transfer, 'status', TRANSFER_STATUS_VALUE.Rejected);
      }

      return transfer;
    });
  }

  public async getBulkSitesModalColumns(
    fromSiteIds: string[],
    modalColumns: IPdfTableHeader[],
    showQohFromOverride: boolean = null,
  ): Promise<IPdfTableHeader[]> {
    let canSelectModalColumns = modalColumns;
    if (!isNil(showQohFromOverride)) {
      if (!showQohFromOverride) {
        canSelectModalColumns = filterQohFromColumn(canSelectModalColumns);
      }
    } else {
      const selectedFromSites = filter(this.companySitesList, (item: ISite) =>
        includes(fromSiteIds, item.id),
      );

      // get DM preferences QOH show or hidden, hidden should filter the QOH column
      const displayType = this.stateService.getTransferDisplayQohFrom();
      if (
        !displayType &&
        every(selectedFromSites, (selectedFromSite) =>
          isSiteTypeWarehouse(selectedFromSite),
        )
      ) {
        // hide QOH column
        canSelectModalColumns = filterQohFromColumn(canSelectModalColumns);
      }
    }

    return canSelectModalColumns;
  }

  public bulkGenerateTransferSummary = (
    isWrittenSheet: boolean = false,
    isExport: boolean = false,
    options?: {
      isTransferGroup?: boolean;
      printAndExportMaxLength?: number;
      showQohFromOverride?: boolean;
      isShownLoadingWhenGenerate?: boolean;
      totalsConfiguration?: TotalsConfiguration;
    },
  ): Function => {
    return async (
      selectedItems: IBaseEntityType[],
      dataBuilder: (
        selectedItems: IBaseEntityType[],
      ) => Promise<IBaseEntityType[]> = null,
    ): Promise<void> => {
      const {
        printAndExportMaxLength = this.printAndExportMaxLength,
        showQohFromOverride,
        isShownLoadingWhenGenerate = true,
        isTransferGroup = false,
        totalsConfiguration: userTotalsConfiguration,
      } = options || {};
      const totalsConfiguration = userTotalsConfiguration;

      if (selectedItems.length > printAndExportMaxLength) {
        this.uiActions.showToasterMessage({
          type: NotificationType.ERROR,
          title: TOASTER_ERROR_MESSAGE.title,
          body: this.i18nService.translate(
            I18n.ErrorMessages.YouCanOnlyOperateUpToMaxCountRecords,
            {
              operate: this.i18nService.translate(
                isExport ? I18n.Common.export : I18n.Common.print,
              ),
              maxCount: printAndExportMaxLength,
            },
          ),
        });

        return;
      }
      this.transferPdfService.setQOHData([]);
      this.transferPdfService.setPendingQty({});
      const isOpenTransfer = every(selectedItems, (transfer) =>
        isEqual(transfer.status, TRANSFER_STATUS_VALUE.Open),
      );

      const sortedSelectItems = sortBy(selectedItems, 'internal_number');
      const pdfBulkData = isFunction(dataBuilder)
        ? async (): Promise<IPdfData[]> => dataBuilder(sortedSelectItems)
        : sortedSelectItems;

      try {
        if (isWrittenSheet) {
          const pdfConfig = await this.getWrittenSheetPdfConfig(
            async (definitions: IPdfDefinitions, bulkData) => {
              return this.getWrittenSheetPdfData(
                bulkData,
                definitions?.options,
                {
                  showQohFromOverride,
                  isGetUpdatedItems: isShownLoadingWhenGenerate,
                  noLoader: true,
                },
              );
            },
            !isOpenTransfer,
            {
              data: pdfBulkData,
            },
            {
              showQoh: !isNil(showQohFromOverride)
                ? showQohFromOverride
                : this.getBulkSitesDisplayQoh(map(selectedItems, 'site_id')),
              isTransferGroup,
              totalsConfiguration,
            },
          );

          await this.pdfGenerateServiceV2.printPdfAsync(
            merge({}, pdfConfig, <IPdfDefinitions>{
              options: {
                isShownLoadingWhenGenerate:
                  isShownLoadingWhenGenerate ??
                  pdfConfig?.options?.isShownLoadingWhenGenerate,
              },
            }),
          );
        } else {
          const allSelectedItems = [];
          forEach(selectedItems, (item) => {
            const isNotFinalTransfer =
              this.transferPdfService.isNotFinalTransfer(
                item.status,
                item.transfer_type,
              );
            allSelectedItems.push(isNotFinalTransfer);
          });
          // Default use not final columns
          let modalColumns = [...TRANSFER_PDF_HEADERS];
          const transfersStatus = map(selectedItems, 'status');
          let quantityRequestedPath: string =
            TRANSFER_PDF_HEADER_VALUES.Quantity;
          if (
            includes(allSelectedItems, true) &&
            includes(allSelectedItems, false)
          ) {
            // Items include not final and final
            modalColumns = reduce(
              TRANSFER_PDF_HEADERS_FINAL,
              (arr, header) => {
                if (!find(modalColumns, { title: header.title })) {
                  arr.push(header);
                }

                return arr;
              },
              modalColumns,
            );
          } else if (includes(allSelectedItems, false)) {
            // All items is final transfer
            modalColumns = [...TRANSFER_PDF_HEADERS_FINAL];
            quantityRequestedPath = TRANSFER_PDF_HEADER_VALUES.InitialQuantity;
          }

          modalColumns = addQtyPulledColumn(modalColumns, {
            preColumnPath: quantityRequestedPath,
            transferStatus: transfersStatus,
          });
          modalColumns = await this.getBulkSitesModalColumns(
            map(selectedItems, 'site_id'),
            modalColumns,
            showQohFromOverride,
          );

          const transferUnitIndex = findIndex(modalColumns, (header) =>
            isEqual(header.title, I18n.Forms.TransferForm.TransferUnit),
          );
          modalColumns.splice(
            transferUnitIndex + 1,
            0,
            TRANSFER_PAR_QTY_COLUMN_HEADER,
          );
          const pdfConfig = await this.getPdfConfig(
            async (definitions: IPdfDefinitions, bulkData) => {
              return this.getPdfData(bulkData, definitions?.options, {
                showQohFromOverride,
                isGetUpdatedItems: isShownLoadingWhenGenerate,
              });
            },
            modalColumns,
            {
              data: pdfBulkData,
              exportFileName: (): string => {
                let fileNameTruncationText = '';
                let fileNameSuffix = '';
                const isSingleFileExport = isEqual(selectedItems.length, 1);
                if (isSingleFileExport) {
                  const selectedRecord = first(selectedItems);
                  fileNameTruncationText = get(
                    selectedRecord,
                    'site_name_to',
                    '',
                  );
                  fileNameSuffix = get(selectedRecord, 'transaction_date', '');
                }

                return isSingleFileExport
                  ? getSingleExportFileName(
                      this.transferPdfService.pdfEntityString,
                      fileNameTruncationText,
                      fileNameSuffix,
                    )
                  : this.transferPdfService.pdfEntityString;
              },
            },
            {
              isTransferGroup,
              totalsConfiguration: {
                enabled: true,
                getDefaultValue: () => getTotalsDefaultValue(selectedItems),
              },
            },
          );

          set(
            pdfConfig,
            ['options', 'isShownLoadingWhenGenerate'],
            isShownLoadingWhenGenerate ??
              pdfConfig?.options?.isShownLoadingWhenGenerate,
          );

          if (isExport) {
            await this.pdfGenerateServiceV2.downloadPdfAsync(pdfConfig);

            return;
          }

          await this.pdfGenerateServiceV2.printPdfAsync(pdfConfig);
        }
      } catch (e) {
        this.uiActions.disableLoader();
        this.uiActions.showToasterMessage({
          type: NotificationType.ERROR,
          title: TOASTER_ERROR_MESSAGE.title,
          body: this.i18nService.instant(
            I18n.ErrorMessages.CheckInternetAndTryAgain,
          ),
        });
      }
    };
  };

  public async showCannotDuplicatedDialogHandler(
    unavailableItems: IBaseEntityType[],
  ): Promise<void> {
    const unavailableItemsDisplayCount$: BehaviorSubject<number> =
      new BehaviorSubject<number>(0);
    await this.uiActions.promptFormModal(
      transferModalDefinition.cannotDuplicatedModal({
        selectOptions: unavailableItems,
        subTitle: unavailableItemsDisplayCount$.pipe(
          rx.map((count) =>
            this.i18nService.translate(
              I18n.Forms.GridMessages.ShowingEntities,
              {
                count,
                entities: this.i18nService.translate(
                  I18n.Forms.GridMessages.Items,
                ),
              },
            ),
          ),
        ),
        sitesDisplayCount$: unavailableItemsDisplayCount$,
        i18nService: this.i18nService,
      }),
    );
  }

  protected getPayloadBlackList(): string[] {
    return [
      'inventory_items.[each].gl_account_entity_id',
      'inventory_items.[each].gl_account_name',
      'inventory_items.[each].item_name',
      'inventory_items.[each].major_category_entity_id',
      'inventory_items.[each].major_category_name',
      'inventory_items.[each].measure_code',
      'inventory_items.[each].measure_description',
      'inventory_items.[each].measure_factor_value',
      'inventory_items.[each].measure_id',
      'inventory_items.[each].minor_category_entity_id',
      'inventory_items.[each].minor_category_name',
      'inventory_items.[each].major_category_external_id',
      'inventory_items.[each].minor_category_external_id',
    ];
  }

  private getPendingQuantity(
    status: string,
    documentId: string,
    siteId: string,
    siteIdTo: string,
  ): Observable<IBaseEntityResponse<IBaseEntityType>> {
    return this.fetchList(
      {
        filter: `status eq 'pending' and ((site_id eq in(${siteId},${siteIdTo})) or (site_id_to eq in(${siteId},${siteIdTo})))`,
        select: [
          'site_id',
          'site_id_to',
          'inventory_items.item_entity_id',
          'inventory_items.initial_count',
          'inventory_items.measure_factor_value',
        ],
      },
      {
        skipMappers: true,
      },
      true,
    ).pipe(
      rx.map((res) => {
        if (!isEqual(TRANSFER_STATUS_VALUE.Pending, status)) {
          return res;
        }
        const filteredRes = {
          ...res,
          items: filter(res.items, (transfer) => transfer._id !== documentId),
        };

        return filteredRes;
      }),
    );
  }

  private getInventoryItemsEntityId(
    res: IBaseEntityResponse,
    inventoryItems: IInventoryItem[],
    selectedEvent: IEvent,
    transferType: string,
  ): IBaseEntityResponse {
    forEach(res.items, (inventoryItem: InventoryItemVenue) => {
      const measure = find(
        inventoryItems,
        (item) => item.item_entity_id === inventoryItem?.entity_id,
      );
      const measures = inventoryItem.measures || [];
      const transferMeasures = filter(
        measures,
        (item) => item.transactions.transfer,
      );
      // sort the transfer Unit by description
      const sortMeasures = sortBy(
        map(transferMeasures, (filterItem) => {
          return {
            ...filterItem,
            text: generateUnitTextV2(filterItem, {
              numberFormatService: this.numberFormatService,
              measureTransactionType: MEASURE_TRANSACTION.Transfer,
            }),
            value: filterItem.entity_id,
            description: filterItem.description,
          };
        }),
        (item) => toLower(item.description),
      );
      set(inventoryItem, 'origin_measures', measures);
      set(inventoryItem, 'measures', sortMeasures);
      // if the document have the measureEntityId use it else use first value
      const measureEntityId = get(measure, 'measure_entity_id', null);
      set(
        inventoryItem,
        'measure_entity_id',
        getDefaultOrSelectedUnitEntityId(measureEntityId, sortMeasures),
      );
      const isOutsideOfEventTransfer = isEqual(
        TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER,
        transferType,
      );
      const parQty = getParQty(
        inventoryItem as IQohInventoryItemVenue,
        selectedEvent,
        isOutsideOfEventTransfer,
      );
      set(inventoryItem, 'par_qty', parQty);
    });

    return res;
  }

  private mixinXVIData(
    inventoryItemVenues: InventoryItemVenue[],
    inventoryItems,
  ): IQohInventoryItemVenue[] {
    if (isEmpty(inventoryItemVenues)) {
      return [];
    }

    return map(inventoryItemVenues, (item): IQohInventoryItemVenue => {
      const virtualItem = find(
        inventoryItems,
        (vItem) => vItem.item_entity_id === item.entity_id,
      );

      return {
        ...item,
        'gl_account.name': get(item, 'gl_account.name'),
        initial_count: get(virtualItem, 'initial_count', 0),
        item_cost: get(
          virtualItem,
          'item_cost',
          calculateUnitCost({ data: { ...item } }),
        ),
        total_cost: get(virtualItem, 'total_cost', 0),
      } as unknown as IQohInventoryItemVenue;
    });
  }

  private addCountAndErrorToItems(
    items: IQohInventoryItemVenue[],
    inventoryItems,
  ): IQohInventoryItemVenue[] {
    const inventoryItemVenueQuantityMap = reduce(
      inventoryItems,
      (itemMap, item) => ({
        ...itemMap,
        [item.item_entity_id]: {
          count: item.count,
          initial_count: item.initial_count,
        },
      }),
      {},
    );
    const mapperItem = this.getUpdatedItemsLevel(inventoryItems);

    return map(items, (item) => {
      item._id = '';
      const count = get(
        inventoryItemVenueQuantityMap,
        `${item.entity_id}.count`,
      );
      const initialCount = get(
        inventoryItemVenueQuantityMap,
        `${item.entity_id}.initial_count`,
      );

      return Object.assign({}, item, {
        count,
        initial_count: initialCount,
        error: mapperItem[item.entity_id]
          ? mapperItem[item.entity_id].error
          : false,
        _id: mapperItem[item.entity_id] ? mapperItem[item.entity_id]._id : '',
      });
    });
  }

  private getUpdatedItemsLevel(inventoryItems): ITransferErrorMapper {
    const mapper = reduce(
      inventoryItems,
      (itemMap, item) => {
        return {
          ...itemMap,
          [item.item_entity_id]: {
            error: item.error ?? false,
            _id: item._id,
          },
        };
      },
      {},
    );

    return mapper;
  }

  public async duplicate(transfers?: ITransferData[]): Promise<void> {
    if (isEmpty(transfers)) {
      return;
    }

    await this.getSelectedEventInfo({
      beforeFormClose: async (confirmed, modalValue) =>
        this.handleDuplicateEventSelected({
          transfers,
          confirmed,
          modalValue,
        }),
    });
  }

  public async getSelectedEventInfo(options?: {
    beforeFormClose?: Function;
  }): Promise<{ eventId: string }> {
    const { confirmed, value } = await this.uiActions.promptFormModal({
      ...transferModalDefinition.duplicatePrepareModal({
        i18nService: this.i18nService,
      }),
      beforeFormClose: options?.beforeFormClose,
    });

    if (!confirmed) {
      return;
    }

    const eventId: string = head(value.events ?? []);

    return { eventId };
  }

  public async handleDuplicateEventSelected(options: {
    confirmed: boolean;
    modalValue: Record<string, Primitive | string[]>;
    transfers: ITransferData[];
  }): Promise<boolean> {
    const { confirmed, modalValue, transfers } = options;

    if (!confirmed) {
      return true;
    }

    const selectEventInfo = {
      eventId: head((modalValue.events as string[]) ?? []) as string,
    };

    const transferIds = map(transfers, '_id');

    let duplicatedTransfers = [];

    try {
      this.uiActions.preventLoaderCloseWhenTimeout(true);
      this.uiActions.enableLoader();
      duplicatedTransfers = await this.handleTransfersDuplicate(
        selectEventInfo.eventId,
        transferIds,
      );
    } catch (error) {
    } finally {
      this.uiActions.disableLoader();
    }

    if (
      transferIds.length === 1 &&
      !isEmpty(head(duplicatedTransfers)?.error)
    ) {
      await this.uiActions.confirm({
        title: I18n.Common.Notice,
        text: head(duplicatedTransfers).error,
        buttons: [{ ...BTN_YES, caption: I18n.Common.Ok }],
      });

      return false;
    }

    this.apiActions.openDocumentEditor(transferEntityType, 'add', {
      duplicateOption: {
        duplicateTitle: this.i18nService.translate(
          I18n.InfoMessages.DuplicateTitle,
          {
            entityType: this.i18nService.translate(
              getSingularEntityName(ENTITY_TYPES.TRANSFER),
            ),
            internalNumber: get(head(transfers), 'internal_number'),
          },
        ),
        document: head(duplicatedTransfers),
        isDuplicateMode: true,
      },
    });

    return true;
  }

  public async handleTransfersDuplicate(
    eventId: string,
    duplicateTransferIds: string[],
  ): Promise<ITransferData[]> {
    // get the duplicate transfers
    const transfers: ITransferData[] = await this.fetchList({
      filter: `_id eq in(${duplicateTransferIds})`,
      select: [
        'site_id',
        'site_name',
        'site_id_to',
        'site_name_to',
        'site_department_code_to',
        'inventory_items.item_entity_id',
        'inventory_items.item_name',
        'inventory_items.measure_entity_id',
        'inventory_items.measure_factor_value',
        'inventory_items.initial_count',
        'inventory_items.count',
      ],
    })
      .pipe(rx.map((res) => res.items))
      .toPromise();
    // get site info
    const siteIds = [];
    forEach(transfers, (transfer) => {
      siteIds.push(get(transfer, 'site_id'));
      siteIds.push(get(transfer, 'site_id_to'));
    });
    const siteQuery: IFilterQuery = {
      filter: `_id eq in(${uniq(siteIds)})`,
      select: ['name', 'id', 'type', 'business_type', 'store_number'],
      top: MAX_PAGE_SIZE,
      filter_by_assigned_sites: true,
      include_inactive: false,
    };
    const sites: ISite[] = (await this.sitesService
      .fetchList(siteQuery)
      .pipe(
        rx.first(),
        rx.map((res) => res.items),
      )
      .toPromise()) as ISite[];
    const siteMap = keyBy(sites, 'id');

    let transferType = TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER;
    let transactionDate = this.companyService.getCurrentTime();
    let eventSites: ISite[] = [];
    let assignedWarehouseSiteMap = {};
    let assignedEventSiteMap = {};
    let sitePreferenceMap = {};
    let eventInfo = {};
    let eventStatusError: string;
    let isDuringTransfer: boolean = false;

    if (!isEmpty(eventId)) {
      // get event info
      const event = await this.xprtDataService
        .getEventDetailById(eventId)
        .pipe(rx.first())
        .toPromise();
      eventSites = get(event, 'available_sites', []);
      const eventStatus = get(event, 'status');
      eventInfo = {
        event_id: get(event, 'id'),
        event_name: get(event, 'name'),
        event_status: eventStatus,
        event_date: get(head(event?.event_days), 'start_date_time'),
      };

      // get transfer type by event status
      const { type, error } = this.getDuplicateTransferType(eventStatus);
      transferType = type;
      eventStatusError = error;
      isDuringTransfer = isEqual(
        transferType,
        TRANSFER_TYPE.DURING_EVENT_TRANSFER,
      );

      if (isDuringTransfer) {
        transactionDate = this.companyService.convertUTCDateWithTimeZone(
          get(eventInfo, 'event_date'),
          true,
        );
      }

      assignedWarehouseSiteMap = keyBy(
        filter(sites, (site) => isSiteTypeWarehouse(site)),
        'id',
      );
      assignedEventSiteMap = keyBy(
        intersectionBy(sites, eventSites, 'id'),
        'id',
      );

      if (isDuringTransfer) {
        const validateSiteToIds: string[] = [];
        forEach(transfers, (transfer) => {
          if (isSiteTypePrimaryWarehouse(siteMap[transfer.site_id])) {
            validateSiteToIds.push(transfer.site_id_to);
          }
        });

        // get site preferences by site to
        if (!isEmpty(validateSiteToIds)) {
          const sitePreferences = await this.dmDataService
            .getSiteGroupId(validateSiteToIds)
            .pipe(
              rx.switchMap((siteGroupId) =>
                this.dmDataService.getSitesPreferencesBySiteGroupId(
                  siteGroupId,
                ),
              ),
            )
            .toPromise();
          sitePreferenceMap = keyBy(sitePreferences, 'site_id');
        }
      }
    }

    const transferResult = [];
    const availableTransferSites = [];
    const availableTransfers = [];
    // generate unable to duplicate transfers
    forEach(transfers, (transfer) => {
      // event status is not correct
      const isEventStatusError = !isEmpty(eventStatusError);
      // validate the sites is exist and assign to the user
      const notAssignedSites: NotAssignedSites = {};
      const fromSiteName = get(
        siteMap,
        `${transfer.site_id}.name`,
        transfer.site_name,
      );
      const toSiteName = get(
        siteMap,
        `${transfer.site_id_to}.name`,
        transfer.site_name_to,
      );
      let isPrimaryWarehouseError = false;

      if (isEmpty(siteMap[transfer.site_id])) {
        set(notAssignedSites, 'fromSiteName', fromSiteName);
      }
      if (isEmpty(siteMap[transfer.site_id_to])) {
        set(notAssignedSites, 'toSiteName', toSiteName);
      }

      if (!isEmpty(eventId)) {
        // to site is not warehouse and not assigned to the event
        const isToSiteError =
          isEmpty(assignedWarehouseSiteMap[transfer.site_id_to]) &&
          isEmpty(assignedEventSiteMap[transfer.site_id_to]);

        // from site is not warehouse and not assigned to the event
        const isFromSiteError =
          isEmpty(assignedWarehouseSiteMap[transfer.site_id]) &&
          isEmpty(assignedEventSiteMap[transfer.site_id]);
        if (isFromSiteError) {
          set(notAssignedSites, 'fromSiteName', fromSiteName);
        }

        if (isToSiteError) {
          set(notAssignedSites, 'toSiteName', toSiteName);
        }
        // in during event transfer
        // if from site is primary warehouse
        // to site's preferences primary warehouse must be the from site
        if (
          isDuringTransfer &&
          isSiteTypePrimaryWarehouse(siteMap[transfer.site_id])
        ) {
          isPrimaryWarehouseError = !isEqual(
            get(
              sitePreferenceMap[transfer.site_id_to],
              'preferences.back_office.warehouse.primary_warehouse',
            ),
            transfer.site_id,
          );
          if (isEmpty(assignedEventSiteMap[transfer.site_id_to])) {
            set(notAssignedSites, 'toSiteName', toSiteName);
          }
        }
      }

      if (
        isEventStatusError ||
        !isEmpty(notAssignedSites) ||
        isPrimaryWarehouseError
      ) {
        const duplicateError: DuplicateError = {
          notAssignedSites,
          isPrimaryWarehouseError,
          fromSiteName,
          toSiteName,
        };
        transferResult.push({
          transfer_type: transferType,
          site_id: transfer.site_id,
          site_name: get(siteMap, `${transfer.site_id}.name`, ''),
          store_number: get(siteMap, `${transfer.site_id}.store_number`, ''),
          site_id_to: transfer.site_id_to,
          site_name_to: get(siteMap, `${transfer.site_id_to}.name`, ''),
          store_number_to: get(
            siteMap,
            `${transfer.site_id_to}.store_number`,
            '',
          ),
          error:
            eventStatusError ??
            this.getTransferDuplicateErrorMessage(
              duplicateError,
              this.i18nService,
            ),
          ...eventInfo,
        });
      } else {
        availableTransferSites.push(get(transfer, 'site_id'));
        availableTransferSites.push(get(transfer, 'site_id_to'));
        availableTransfers.push(transfer);
      }
    });

    if (isEmpty(availableTransfers)) {
      return transferResult;
    }

    // get inventory items master data
    const inventoryItemMasterMap = await this.inventoryItemVenueService
      .fetchList(
        {
          filter: 'is_active eq true',
          select: ['entity_id', 'name', 'measures'],
        },
        { fetchFullListData: true },
      )
      .pipe(rx.map((res) => keyBy(res.items, 'entity_id')))
      .toPromise();
    // get inventory items the site assignment by current
    const inventoryItemSiteMap = await this.dmDataService
      .getSiteGroupId(availableTransferSites)
      .pipe(
        rx.switchMap((siteGroupId) =>
          this.inventoryItemVenueService.fetchCurrentList(
            { select: ['entity_id', 'site_id'] },
            [],
            siteGroupId,
          ),
        ),
        rx.map((res) =>
          keyBy(
            res.items,
            (item) => `${get(item, 'entity_id')}/${get(item, 'site_id')}`,
          ),
        ),
      )
      .toPromise();

    // for each the duplicate transfers
    forEach(availableTransfers, (transfer) => {
      const { inventoryItems, unavailableItems } =
        this.generateInventoryItemsForDuplicate(
          transfer,
          inventoryItemMasterMap,
          inventoryItemSiteMap,
        );
      const duplicateTransfer = {
        transfer_type: transferType,
        transaction_date: transactionDate,
        site_department_code_to: transfer.site_department_code_to,
        site_id: transfer.site_id,
        site_name: get(siteMap, `${transfer.site_id}.name`, ''),
        store_number: get(siteMap, `${transfer.site_id}.store_number`, ''),
        site_id_to: transfer.site_id_to,
        site_name_to: get(siteMap, `${transfer.site_id_to}.name`, ''),
        store_number_to: get(
          siteMap,
          `${transfer.site_id_to}.store_number`,
          '',
        ),
        virtual: {
          inventory_items: inventoryItems,
          duplicate_unavailable_items: unavailableItems,
        },
        ...eventInfo,
      };

      if (isDuringTransfer) {
        set(
          duplicateTransfer,
          'virtual.transaction_date',
          get(eventInfo, 'event_date'),
        );
      }
      transferResult.push(duplicateTransfer);
    });

    return transferResult;
  }

  public getCommonAssignedErrorMessage(duplicateError: DuplicateError): string {
    const { notAssignedSites, fromSiteName, toSiteName } = duplicateError;
    if (notAssignedSites?.fromSiteName && notAssignedSites?.toSiteName) {
      return this.i18nService.translate(
        I18n.InfoMessages.DuplicateSitesNotAvailable,
        {
          fromSiteName,
          toSiteName,
        },
      );
    }

    const siteName =
      notAssignedSites?.fromSiteName || notAssignedSites?.toSiteName;
    if (!isEmpty(siteName)) {
      return this.i18nService.translate(
        I18n.InfoMessages.DuplicateSiteNotAvailable,
        {
          siteName,
        },
      );
    }

    return '';
  }

  public getCommonWarehouseErrorMessage(
    duplicateError: DuplicateError,
  ): string {
    const { fromSiteName, toSiteName } = duplicateError;

    return this.i18nService.translate(
      I18n.InfoMessages.DuplicateWarehouseError,
      {
        toSiteName,
        fromSiteName,
      },
    );
  }

  public getTranslatedErrorMessages(i18nService: XVII18nService): {
    SITES_AND_WAREHOUSE_VERIFY_MESSAGE: string;
    ASSIGNMENT_SITES_VERIFY_MESSAGE: string;
    WAREHOUSE_SETTING_VERIFY_MESSAGE: string;
    AND: string;
  } {
    return {
      SITES_AND_WAREHOUSE_VERIFY_MESSAGE: i18nService.instant(
        I18n.InfoMessages.PleaseVerifySitesAndWarehouse,
      ),
      ASSIGNMENT_SITES_VERIFY_MESSAGE: i18nService.instant(
        I18n.InfoMessages.PleaseVerifyAssignmentSites,
      ),
      WAREHOUSE_SETTING_VERIFY_MESSAGE: i18nService.instant(
        I18n.InfoMessages.PleaseVerifyPrimaryWarehouseSettings,
      ),
      AND: i18nService.instant(I18n.Common.And),
    };
  }

  public getTransferDuplicateErrorMessage(
    duplicateError: DuplicateError,
    i18nService: XVII18nService,
  ): string {
    const {
      SITES_AND_WAREHOUSE_VERIFY_MESSAGE,
      ASSIGNMENT_SITES_VERIFY_MESSAGE,
      WAREHOUSE_SETTING_VERIFY_MESSAGE,
      AND,
    } = this.getTranslatedErrorMessages(i18nService);
    // mixed error
    const { notAssignedSites, isPrimaryWarehouseError } = duplicateError;
    const mixedErrorMessage = `${this.getCommonAssignedErrorMessage(duplicateError)} ${
      isPrimaryWarehouseError
        ? SITES_AND_WAREHOUSE_VERIFY_MESSAGE
        : ASSIGNMENT_SITES_VERIFY_MESSAGE
    }`;
    const atLeastExistOneNotAssignedSite =
      notAssignedSites?.fromSiteName || notAssignedSites?.toSiteName;
    if (isPrimaryWarehouseError) {
      const primaryWarehouseMsg =
        this.getCommonWarehouseErrorMessage(duplicateError);

      if (atLeastExistOneNotAssignedSite) {
        return `${primaryWarehouseMsg} ${AND} ${lowerFirst(mixedErrorMessage)}`;
      }

      return `${primaryWarehouseMsg} ${WAREHOUSE_SETTING_VERIFY_MESSAGE}`;
    }

    if (atLeastExistOneNotAssignedSite) {
      return mixedErrorMessage;
    }

    return '';
  }

  private getDuplicateTransferType(eventStatus: string) {
    let type: TRANSFER_TYPE;
    let error: string;
    if (isEqual(eventStatus, EVENT_STATUS_VALUE.Pending)) {
      type = TRANSFER_TYPE.PRE_EVENT_TRANSFER;
    } else if (isEqual(eventStatus, EVENT_STATUS_VALUE.Open)) {
      type = TRANSFER_TYPE.DURING_EVENT_TRANSFER;
    } else {
      type = TRANSFER_TYPE.DURING_EVENT_TRANSFER;
      error = this.i18nService.translate(
        I18n.InfoMessages.CannotDuplicateTransferDueToEventClosed,
      );
    }

    return {
      type,
      error,
    };
  }

  private getInvalidItemReasons(
    transfer: ITransferData,
    inventoryItem: IInventoryItemVenue,
    siteAssignment: { from: ISite; to: ISite },
  ): { reasons: string[]; notAssignedSites: string[] } {
    const reasons = [];
    const notAssignedSites = [];
    if (isEmpty(inventoryItem)) {
      reasons.push(DUPLICATE_TRANSFER_ERROR_KEY.ITEM_NOT_EXIST);
    } else {
      // validate the inventory item has transfer unit
      const hasTransferUnit = some(inventoryItem.measures, [
        'transactions.transfer',
        true,
      ]);

      // validate the inventory item site assignment
      if (isEmpty(siteAssignment.from)) {
        notAssignedSites.push(transfer.site_id);
      }
      if (isEmpty(siteAssignment.to)) {
        notAssignedSites.push(transfer.site_id_to);
      }

      // generate the error
      if (!hasTransferUnit) {
        reasons.push(DUPLICATE_TRANSFER_ERROR_KEY.ITEM_TRANSFER_UNIT_NOT_EXIST);
      }
      if (!isEmpty(notAssignedSites)) {
        reasons.push(DUPLICATE_TRANSFER_ERROR_KEY.ITEM_NOT_ASSIGN_SITE);
      }
    }

    return {
      reasons,
      notAssignedSites,
    };
  }

  private generateInventoryItemsForDuplicate(
    transfer: ITransferData,
    inventoryItemMasterMap,
    inventoryItemSiteMap,
  ) {
    const transferInventoryItems = get(transfer, 'virtual.inventory_items', []);
    const inventoryItems = [];
    const unavailableItems = [];
    forEach(transferInventoryItems, (item: IInventoryItemVenue) => {
      // generate transfer inventory items and unavailable items
      const inventoryItem: IInventoryItemVenue = get(
        inventoryItemMasterMap,
        item.item_entity_id,
        {},
      ) as IInventoryItemVenue;

      const siteAssignment = {
        from: get(
          inventoryItemSiteMap,
          `${item.item_entity_id}/${transfer.site_id}`,
        ),
        to: get(
          inventoryItemSiteMap,
          `${item.item_entity_id}/${transfer.site_id_to}`,
        ),
      };

      const { reasons, notAssignedSites } = this.getInvalidItemReasons(
        transfer,
        inventoryItem,
        siteAssignment,
      );

      if (isEmpty(reasons)) {
        const measureByDuplicate = find(
          inventoryItem.measures,
          (measure) =>
            isEqual(measure.entity_id, item.measure_entity_id) &&
            get(measure, 'transactions.transfer', false),
        );

        inventoryItems.push({
          item_entity_id: get(item, 'item_entity_id'),
          measure_entity_id: item.measure_entity_id,
          measure_factor_value: item.measure_factor_value,
          is_measure_changed:
            isNil(measureByDuplicate) ||
            !isEqual(
              get(measureByDuplicate, 'factor_value'),
              item.measure_factor_value,
            ),
          initial_count: item.initial_count ?? item.count,
          is_duplicated: true,
          measures: get(inventoryItem, 'measures'),
        });
      } else {
        unavailableItems.push({
          item_entity_id: get(item, 'item_entity_id'),
          item_name: get(inventoryItem, 'name', get(item, 'item_name')),
          not_assigned_sites: notAssignedSites,
          reasons,
        });
      }
    });

    return {
      inventoryItems,
      unavailableItems,
    };
  }
  // eslint-disable-next-line max-lines
}
